import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { BACKEND_URL, API_ENDPOINTS } from '../../utils/apiConfig';
import { useMaterialReservation } from './MaterialReservationContext';
import { Typography, CircularProgress, Grid, TextField, Box, Stack, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Checkbox, FormControlLabel, Tooltip, Collapse, Menu, MenuItem, useMediaQuery, useTheme } from '@mui/material';
import GereserveerdMateriaalLijst from './GereserveerdMateriaalLijst';
import SimpleMaterialWizard from './SimpleMaterialWizard';
import ReserveringsData from './ReserveringsData';
import { toast } from 'react-hot-toast';
import CopyMaterialsDialog from './CopyMaterialsDialog';
import CopyToOtherDraaidagenDialog from './CopyToOtherDraaidagenDialog';
import ClashApprovalDialog from './ClashApprovalDialog';
import TypePopup from '../../pages/MateriaalBeheer/TypePopup';
import ExemplaarPopup from '../../pages/MateriaalBeheer/ExemplaarPopup';
import { crewSamengevoegd } from '../../utils/draaidagCrew';

const T = { primary: '#526075', primaryDim: '#465469', primaryContainer: '#d5e3fd', tertiary: '#635983', tertiaryContainer: '#dbcdff', surface: '#ffffff', surfaceLow: '#f1f4f5', surfaceHigh: '#e5e9eb', text: '#2d3335', secondary: '#556071', outline: '#767c7e', outlineVariant: '#adb3b5', emerald: '#10b981', emeraldBg: '#ecfdf5', error: '#a83836', errorBg: '#fef2f2', amber: '#f59e0b', amberBg: '#fef3c7', font: '"Manrope", system-ui, sans-serif', fontBody: '"Inter", system-ui, sans-serif', shadow: '0 1px 3px rgba(45,51,53,0.04)' };
const Icon = ({ name, size = 24, sx = {} }) => (<span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 400", ...sx }}>{name}</span>);

// Strip auto-generated Outlook "Microsoft Teams-vergadering" blokken uit een draaidag-omschrijving.
// Outlook plakt deze automatisch in de agenda-uitnodiging; ze voegen niks toe in de materiaal-app.
// Het blok wordt begrensd door lange underscore-lijnen (~80) met een kortere (~32) separator ertussen;
// de drempel van 40+ slaat de binnen-separator over zodat het hele blok in één keer verdwijnt.
const stripOutlookTeamsBlocks = (text) => {
    if (!text || typeof text !== 'string') return '';
    return text
        .replace(/_{40,}\s*Microsoft Teams[\s\S]*?_{40,}/gi, '')   // volledig blok (met sluit-lijn)
        .replace(/_{40,}\s*Microsoft Teams[\s\S]*$/i, '')          // afgekapt eind-blok zonder sluit-lijn
        .replace(/\n{3,}/g, '\n\n')                                // overgebleven witruimte opschonen
        .trim();
};

export const MaterialenOverzicht = React.memo(function MaterialenOverzicht({ updateDraaidagLocally, fetchDraaidagenFromBackend, draaidagen = [] }) {
    const {
        selectedDraaidag,
        setSelectedDraaidag,
        reservations,
        fetchReservations,
        setCurrentReservation,
        currentReservation,
        loading: reservationsLoading,
        error,
        updateReservationField
    } = useMaterialReservation();

    // Ref to avoid re-creating loadAllDataParallel when reservation changes
    const currentReservationRef = useRef(currentReservation);
    currentReservationRef.current = currentReservation;
    const fetchCounterRef = useRef(0); // Guard against stale fetches

    // Memoize the current reservation lookup (NU BOVENAAN)
    const currentReservationMemo = useMemo(() => {
        return currentReservation;
    }, [currentReservation]);

    const [alleMaterialen, setAlleMaterialen] = useState([]);
    const [isMaterialsLoading, setIsMaterialsLoading] = useState(false);
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [materialsCache, setMaterialsCache] = useState({ data: null, timestamp: null });
    const MATERIALS_CACHE_DURATION = 5 * 60 * 1000; // 5 minuten cache
    const [openWizard, setOpenWizard] = useState(false);
    const [selectedMaterials, setSelectedMaterials] = useState([]);
    const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(0);
    const [notitie, setNotitie] = useState('');
    const [notitieStatus, setNotitieStatus] = useState('');
    const [isWaitingForNotitieSync, setIsWaitingForNotitieSync] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const debounceRef = useRef();
    const saveTimeoutRef = useRef();
    const lastSavedValue = useRef('');
    const [geenMateriaalNodig, setGeenMateriaalNodig] = useState(false);
    const [descExpanded, setDescExpanded] = useState(false);
    // Clash-goedkeuring bij +1/-1 verlenging
    const [clashDialogOpen, setClashDialogOpen] = useState(false);
    const [clashGroups, setClashGroups] = useState([]);
    const [pendingExtension, setPendingExtension] = useState(null);
    // Losse reservering verplaatsen (datum wijzigen) — hergebruikt dezelfde clash-dialog
    const [moveMode, setMoveMode] = useState(false);
    const [moveStart, setMoveStart] = useState('');
    const [moveEnd, setMoveEnd] = useState('');
    const [pendingMove, setPendingMove] = useState(null);
    const moveToHandledRef = useRef(null);

    // Redesign: responsive + context-balk + "Meer"-menu
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [moreAnchor, setMoreAnchor] = useState(null);
    useEffect(() => { setDetailsOpen(!isMobile); }, [isMobile]);

    // geenMateriaalNodig: only true when explicitly flagged, not when materials happen to be empty
    useEffect(() => {
        setGeenMateriaalNodig(selectedDraaidag?.fields?.['Geen Materiaal Nodig'] || false);
    }, [selectedDraaidag?.id]);
    const [opslaanStatus, setOpslaanStatus] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [copyDialogOpen, setCopyDialogOpen] = useState(false);
    const [copyToDialogOpen, setCopyToDialogOpen] = useState(false);
    const [materiaalsets, setMateriaalsets] = useState([]);
    const [materiaalTypes, setMateriaalTypes] = useState([]);
    // Info-popup (compleet materiaal van een exemplaar) — hergebruik TypePopup uit materiaalbeheer
    const [infoTypeId, setInfoTypeId] = useState(null);
    const [infoExemplaarId, setInfoExemplaarId] = useState(null);
    const [saveAsSetDialogOpen, setSaveAsSetDialogOpen] = useState(false);
    const [newSetName, setNewSetName] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [extraDagNa, setExtraDagNa] = useState(false);
    const [extraDagVoor, setExtraDagVoor] = useState(false);
    const [accountInfo, setAccountInfo] = useState(null); // { naam, beeldmerkUrl }
    const accountCacheRef = useRef({}); // { accountId: { naam, beeldmerkUrl } }

    // Resolve material ID from OLD base via backend (security: API key not exposed)
    const resolveOldBaseMaterialRecordId = useCallback(async (material) => {
        try {
            const materialId = material?.fields?.ID || '';
            const materialName = material?.fields?.Naam || '';

            if (!materialId && !materialName) return null;

            const params = new URLSearchParams();
            if (materialId) params.append('materialId', materialId);
            if (materialName) params.append('materialName', materialName);

            const response = await axios.get(
                `${BACKEND_URL}/Api/materialen/resolve-old-base?${params.toString()}`
            );

            return response.data?.recordId || null;
        } catch (e) {
            console.error('resolveOldBaseMaterialRecordId failed:', e.response?.data || e.message);
            return null;
        }
    }, []);

    // Use a Set for faster lookups
    const selectedMaterialsSet = useMemo(() => {
        return new Set(selectedMaterials.map(m => m.id));
    }, [selectedMaterials]);

    // Airtable base ID voor links
    const AIRTABLE_BASE_ID = 'apphMT5j6gtUnMY1p';
    const AIRTABLE_PROJECT_PAGE = 'pagiiQRyyfYj7RHbl';
    const AIRTABLE_CALLSHEET_PAGE = 'pagEOROTuaYMwBBKi';
    const AIRTABLE_CALLSHEET_TABLE = 'tblsyhoYyJOGZiW1T';

    // Optimistic preview van draaidag data
    const optimisticDraaidagData = useMemo(() => {
        if (!selectedDraaidag) return null;

        const fields = selectedDraaidag.fields || {};
        const originalTitle = fields.Draaidag || '';

        // Haal projectnummer uit de titel als ProjectNumRegex niet beschikbaar is
        const projectNumFromTitle = originalTitle.match(/\[(\d+)\]/)?.[1] || null;

        // Haal projectnaam uit titel (tussen || en [nummer])
        const projectNaamFromTitle = originalTitle.match(/\|\|\s*([^-\[]+)/)?.[1]?.trim() || '';

        // Klantnaam uit titel: probeer || separator, dan / separator
        const klantNaamFromTitle = originalTitle.match(/\|\|\s*([^+\-\[]+)/)?.[1]?.trim() ||
                                   originalTitle.split('/').pop()?.match(/^\s*([^-\[]+)/)?.[1]?.trim() || '';
        // Account record ID (direct van draaidag via SourceRecordID Account)
        const klantAccountId = fields.AccountRecordId || null;

        // Crew: probeer lookup velden, dan titel parsing
        const isName = (v) => typeof v === 'string' && !v.startsWith('rec');
        const crewLookup = fields['Naam (from Crew)'];
        const crewFromLookup = Array.isArray(crewLookup) && crewLookup.every(isName) ? crewLookup.join(' + ') : '';
        const crewField = fields.Camerapersonen;
        const crewFromField = Array.isArray(crewField) && crewField.every(isName) ? crewField.join(' + ') : (typeof crewField === 'string' ? crewField : '');
        // Gekoppelde crew + niet-gekoppelde namen uit de titel (externe crew "(b)")
        const crewNaam = crewSamengevoegd(crewFromLookup ? crewLookup : (crewFromField ? crewField : []), originalTitle).label;

        // Project: probeer lookup naam velden
        const projectNaamArr = fields['Projectnaam (from Project)'] || fields['ProjectNaam (from Project)'] || [];
        const projectNaam = (Array.isArray(projectNaamArr) ? projectNaamArr[0] : projectNaamArr) ||
                           fields.ProjectNaam ||
                           fields['Naam (from Project)'] ||
                           projectNaamFromTitle ||
                           '';

        // Project record ID voor link
        const projectRecordId = Array.isArray(fields.Project) ? fields.Project[0] : fields.Project || null;

        // Callsheet: check of er een callsheet is gekoppeld
        // Probeer verschillende veldnamen voor de callsheet linked record
        const callsheetRecordId = Array.isArray(fields.Callsheet) ? fields.Callsheet[0] :
                                  Array.isArray(fields['Callsheets']) ? fields['Callsheets'][0] :
                                  Array.isArray(fields['CallSheet']) ? fields['CallSheet'][0] : null;


        // PDF veld kan attachment zijn (array met url) of lookup
        // Probeer verschillende veldnamen - "Callsheets PDFs" is de correcte naam
        const pdfField = fields['Callsheets PDFs'] ||
                        fields['PDF Callsheet'] ||
                        fields['PDF (from Callsheet)'] ||
                        fields['PDF (from CallSheet)'] ||
                        fields['CallsheetPDF'] ||
                        fields['Callsheet PDF'] ||
                        fields['PDF'] ||
                        null;

        // Als het een attachment is, haal de URL eruit
        let callsheetPdfUrl = null;
        if (pdfField) {
            if (Array.isArray(pdfField) && pdfField.length > 0) {
                // Attachment veld: [{url: '...', filename: '...'}]
                callsheetPdfUrl = pdfField[0]?.url || pdfField[0];
            } else if (typeof pdfField === 'string') {
                callsheetPdfUrl = pdfField;
            }
        }

        return {
            id: selectedDraaidag.id,
            title: originalTitle,
            weergaveNaam: fields.WeergaveNaamMateriaalApp || '',
            displayTitle: fields.WeergaveNaamMateriaalApp || originalTitle || 'Onbekende draaidag',
            startDate: fields.StartDatum,
            endDate: fields.EindDatum,
            description: stripOutlookTeamsBlocks(fields.Description),
            projectType: fields.ProjectType?.[0] || fields['Projecttype (from Project)']?.[0] || 'Onbekend',
            geenMateriaalNodig: currentReservation?.fields?.GeenMateriaalNodig || fields['Geen Materiaal Nodig'] || false,
            notities: currentReservation?.fields?.Notities || '',
            // Extra velden
            crew: crewNaam,
            // Projectnummer: probeer lookup veld (array), dan regex uit titel
            projectNummer: (Array.isArray(fields['Projectnummer (from Project)']) && fields['Projectnummer (from Project)'][0]) ||
                          (fields.ProjectNumRegex && fields.ProjectNumRegex > 0 ? fields.ProjectNumRegex : null) ||
                          projectNumFromTitle || '',
            projectNaam: projectNaam,
            projectRecordId: projectRecordId,
            klantNaam: klantNaamFromTitle,
            klantAccountId: klantAccountId,
            aantalDagen: fields['Aantal dagen'] || fields.Dagen || '',
            duurUren: fields.DuurUren || '',
            // Callsheet info
            callsheetRecordId: callsheetRecordId,
            callsheetPdfUrl: callsheetPdfUrl
        };
    }, [selectedDraaidag, currentReservation]);

    // Fetch account info (naam + beeldmerk) voor klant link
    const klantAccountId = optimisticDraaidagData?.klantAccountId;
    useEffect(() => {
        if (!klantAccountId) { setAccountInfo(null); return; }
        if (accountCacheRef.current[klantAccountId]) {
            setAccountInfo(accountCacheRef.current[klantAccountId]);
            return;
        }
        axios.get(`${BACKEND_URL}/Api/klanten/${klantAccountId}`)
            .then(res => {
                const f = res.data?.account?.fields;
                if (!f) return;
                const beeldmerk = Array.isArray(f.Beeldmerk) && f.Beeldmerk[0];
                const info = {
                    naam: f.Account || '',
                    beeldmerkUrl: beeldmerk?.thumbnails?.small?.url || beeldmerk?.url || null,
                };
                accountCacheRef.current[klantAccountId] = info;
                setAccountInfo(info);
            })
            .catch(() => setAccountInfo(null));
    }, [klantAccountId]);

    // State voor editable weergavenaam
    const [editWeergaveNaam, setEditWeergaveNaam] = useState('');
    const [isEditingNaam, setIsEditingNaam] = useState(false);
    const savingWeergaveNaamRef = useRef(false);

    // Sync weergaveNaam from draaidag
    useEffect(() => {
        setEditWeergaveNaam(selectedDraaidag?.fields?.WeergaveNaamMateriaalApp || '');
    }, [selectedDraaidag?.id]);

    // Handler voor opslaan weergavenaam — slaat op bij de reservering, niet de draaidag
    const handleSaveWeergaveNaam = useCallback(async () => {
        if (savingWeergaveNaamRef.current || !selectedDraaidag?.id) return;

        const currentValue = selectedDraaidag.fields?.WeergaveNaamMateriaalApp || '';
        const newValue = editWeergaveNaam || '';

        if (currentValue === newValue) {
            setIsEditingNaam(false);
            return;
        }

        savingWeergaveNaamRef.current = true;
        try {
            // Opslaan op draaidag (Airtable + PostgreSQL)
            await axios.patch(`${BACKEND_URL}/Api/draaidagen/${selectedDraaidag.id}`, {
                fields: { 'WeergaveNaamMateriaalApp': editWeergaveNaam || null }
            });

            // Update lokale state
            const updatedDraaidag = {
                ...selectedDraaidag,
                fields: { ...selectedDraaidag.fields, WeergaveNaamMateriaalApp: editWeergaveNaam || null }
            };
            setSelectedDraaidag(updatedDraaidag);
            if (typeof updateDraaidagLocally === 'function') updateDraaidagLocally(updatedDraaidag);

            setIsEditingNaam(false);
            toast.success(editWeergaveNaam ? 'Weergavenaam opgeslagen!' : 'Weergavenaam gewist');
        } catch (error) {
            console.error('Fout bij opslaan weergavenaam:', error);
            toast.error('Fout bij opslaan weergavenaam');
        } finally {
            savingWeergaveNaamRef.current = false;
        }
    }, [selectedDraaidag, editWeergaveNaam, setSelectedDraaidag, updateDraaidagLocally]);

    // Parallel data fetching voor snellere laadtijden
    const loadAllDataParallel = useCallback(async (showLoading = true) => {
        if (!selectedDraaidag) return;

        const fetchId = ++fetchCounterRef.current; // Guard against stale responses

        if (showLoading) {
            setIsSaving(true);
        }

        // ── Losse reservering (geen draaidag): laad materialen-pool + evt. bestaande reservering ──
        if (selectedDraaidag.__standalone) {
            try {
                const matsResp = await axios.get(`${BACKEND_URL}/Api/materialen`);
                if (fetchId !== fetchCounterRef.current) return;
                const materials = matsResp.data || [];
                setAlleMaterialen(materials);
                const rid = selectedDraaidag.reservationId || currentReservationRef.current?.id;
                if (rid) {
                    try {
                        const r = await axios.get(API_ENDPOINTS.reservations.get(rid));
                        if (fetchId !== fetchCounterRef.current) return;
                        const f = r.data?.fields || r.data || {};
                        const selIds = f.Materiaal || f.materialIds || [];
                        // Junctions ophalen voor JunctionID + pak-status (zodat "toewijzen" werkt)
                        let junctionByMat = new Map();
                        try {
                            const jResp = await axios.get(API_ENDPOINTS.junctions.list(), { params: { reservationId: rid } });
                            if (fetchId !== fetchCounterRef.current) return;
                            for (const j of (Array.isArray(jResp.data) ? jResp.data : [])) {
                                const matId = Array.isArray(j?.fields?.Materiaal) ? j.fields.Materiaal[0] : null;
                                if (matId) junctionByMat.set(matId, { id: j.id, statuses: j.fields?.StatusJunction || [] });
                            }
                        } catch (je) { /* junctions optioneel */ }
                        const byId = new Map(materials.map(m => [m.id, m]));
                        const sel = selIds.map(id => byId.get(id)).filter(Boolean)
                            .map(m => {
                                const jn = junctionByMat.get(m.id);
                                return { ...m, fields: { ...m.fields, JunctionID: jn?.id || m.fields?.JunctionID, JunctionStatus: jn?.statuses || m.fields?.JunctionStatus || [] } };
                            });
                        setSelectedMaterials(sel);
                        setCurrentReservation(r.data);
                    } catch (e) { setSelectedMaterials([]); }
                } else {
                    setSelectedMaterials([]);
                }
            } catch (e) {
                console.error('[Losse reservering] laden mislukt:', e.message);
            } finally {
                if (fetchId === fetchCounterRef.current) { setIsDataLoaded(true); setIsSaving(false); }
            }
            return;
        }

        try {
            // Use ref to avoid dependency on currentReservation (prevents infinite loop)
            const reservationId = currentReservationRef.current?.id;
            const baseUrl = API_ENDPOINTS.draaidagData(selectedDraaidag.id);
            const url = reservationId
                ? `${baseUrl}?reservationId=${reservationId}`
                : baseUrl;

            const response = await axios.get(url);

            // Guard: if another fetch started while we were waiting, discard this result
            if (fetchId !== fetchCounterRef.current) return;

            const { materials, junctionData } = response.data;
            const realJunctions = Array.isArray(junctionData)
                ? junctionData.filter(j => j && j.id && !String(j.id).startsWith('synthetic_'))
                : [];

            // Build fast lookup map: materialId -> { junctionId, statuses }
            const junctionByMaterialId = new Map();
            for (const j of realJunctions) {
                const matId = Array.isArray(j?.fields?.Materiaal) ? j.fields.Materiaal[0] : null;
                if (matId) {
                    junctionByMaterialId.set(matId, { id: j.id, statuses: j.fields?.StatusJunction || [] });
                }
            }
            setAlleMaterialen(materials);

            if (currentReservationRef.current && materials.length > 0) {
                // Check for legacy reservation (text-based material linking)
                const legacyJunction = junctionData.find(j => j._legacyReservation);

                let initiallySelected = [];

                if (legacyJunction) {
                    // Handle legacy reservation with text-based material names
                    const materialNames = legacyJunction._materialNames || [];

                    // Match material names to material IDs
                    initiallySelected = materials.filter(material => {
                        const materialName = material.fields?.ID || material.fields?.Naam || '';
                        return materialNames.some(name =>
                            materialName.toLowerCase().includes(name.toLowerCase()) ||
                            name.toLowerCase().includes(materialName.toLowerCase())
                        );
                    });
                } else {
                    // Prefer REAL junctionData as the source of truth
                    const reservedIdsFromJunction = new Set(
                        realJunctions
                            .filter(j => Array.isArray(j.fields?.Materiaal))
                            .map(j => j.fields.Materiaal[0])
                    );

                    if (reservedIdsFromJunction.size > 0) {
                        initiallySelected = materials.filter(m => reservedIdsFromJunction.has(m.id));
                    } else {
                        // Fallback to legacy Materiaal field on reservation (array of IDs)
                        const reservedIds = new Set(currentReservation.fields?.Materiaal || []);
                        initiallySelected = materials.filter(m => reservedIds.has(m.id));
                    }
                }


                // Add junction data to materials using the map
                const materialsWithJunction = initiallySelected.map(material => {
                    const j = junctionByMaterialId.get(material.id);
                    if (j) {
                        return {
                            ...material,
                            fields: {
                                ...material.fields,
                                JunctionID: j.id,
                                JunctionStatus: j.statuses
                            }
                        };
                    }
                    return material;
                });

                setSelectedMaterials(materialsWithJunction);

                // Also update alleMaterialen with junction data for consistency
                const updatedAlleMaterialen = materials.map(material => {
                    const j = junctionByMaterialId.get(material.id);
                    if (j) {
                        return {
                            ...material,
                            fields: {
                                ...material.fields,
                                JunctionID: j.id,
                                JunctionStatus: j.statuses
                            }
                        };
                    }
                    return material;
                });
                setAlleMaterialen(updatedAlleMaterialen);
            } else {
                setSelectedMaterials([]);
            }

        } catch (error) {
            // Only log — don't show toast for transient fetch errors (e.g. token race, stale call)
            // Data will be retried automatically when currentReservation is set
            console.warn('Error loading data (will retry):', error.message);
        } finally {
            if (showLoading) {
                setIsSaving(false);
            }
            setIsDataLoaded(true);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDraaidag?.id]); // Only re-create when draaidag changes, not reservation (uses ref)

    // Fetch materiaalsets
    const fetchMateriaalsets = useCallback(async () => {
        try {
            const response = await axios.get(`${BACKEND_URL}/Api/materiaalsets`);
            setMateriaalsets(response.data);
        } catch (error) {
            console.error('Error fetching materiaalsets:', error);
            toast.error('Fout bij ophalen van materiaalsets');
        }
    }, []);

    const fetchMateriaalTypes = useCallback(async () => {
        try {
            const response = await axios.get(`${BACKEND_URL}/Api/materiaaltypes`);
            setMateriaalTypes(response.data?.records || response.data || []);
        } catch (error) {
            console.error('Error fetching materiaaltypes:', error);
        }
    }, []);

    // Map type airtable_id -> type-record (voor icoon/kleur/no-reserv lookup vanuit een exemplaar)
    const typeById = useMemo(() => {
        const m = {};
        materiaalTypes.forEach(t => { m[t.id] = t; });
        return m;
    }, [materiaalTypes]);

    const getTypeForMaterial = useCallback((material) => {
        const raw = material?.fields?.MateriaalType;
        const tid = Array.isArray(raw) ? raw[0] : raw;
        return tid ? typeById[tid] : null;
    }, [typeById]);

    // Bouw de TypePopup-group (compleet materiaal + al zijn exemplaren) voor een exemplaar
    const buildTypeGroup = useCallback((typeId) => {
        const typeRecord = typeById[typeId];
        if (!typeRecord) return null;
        const children = alleMaterialen.filter(m => {
            const raw = m?.fields?.MateriaalType;
            return (Array.isArray(raw) ? raw[0] : raw) === typeId;
        });
        return { type: typeRecord, typeFields: typeRecord.fields || {}, children, categorie: typeRecord.fields?.Categorie || 'Overig' };
    }, [typeById, alleMaterialen]);

    // Refresh na bewerking in de info-popup: types + exemplaar-velden direct verversen
    // ZONDER de selectie of pak-status (JunctionStatus/JunctionID) te verliezen.
    const refreshTypesAndMaterials = useCallback(async () => {
        await fetchMateriaalTypes();
        try {
            const resp = await axios.get(`${BACKEND_URL}/Api/materialen?includeArchived=true`);
            const fresh = Array.isArray(resp.data) ? resp.data : [];
            const byId = {};
            fresh.forEach(m => { byId[m.id] = m; });
            const mergeFields = (m) => {
                const f = byId[m.id];
                if (!f) return m;
                return { ...m, fields: { ...m.fields, ...f.fields, JunctionStatus: m.fields?.JunctionStatus, JunctionID: m.fields?.JunctionID } };
            };
            setAlleMaterialen(prev => {
                const existing = new Set(prev.map(m => m.id));
                const merged = prev.map(mergeFields).filter(m => byId[m.id]);
                const appended = fresh.filter(f => !existing.has(f.id));
                return [...merged, ...appended];
            });
            setSelectedMaterials(prev => prev.map(mergeFields));
        } catch (e) { /* ignore */ }
    }, [fetchMateriaalTypes]);

    const handleOpenTypeInfo = useCallback((material) => {
        const raw = material?.fields?.MateriaalType;
        const tid = Array.isArray(raw) ? raw[0] : raw;
        if (tid) setInfoTypeId(tid);
    }, []);

    const infoGroup = useMemo(() => (infoTypeId ? buildTypeGroup(infoTypeId) : null), [infoTypeId, buildTypeGroup]);
    const infoExemplaar = useMemo(() => (infoExemplaarId ? alleMaterialen.find(m => m.id === infoExemplaarId) || null : null), [infoExemplaarId, alleMaterialen]);
    const getMaterialReservationsForPopup = useCallback((materialId) => {
        return (reservations || []).filter(r => (r.fields?.Materiaal || []).includes(materialId)).map(r => ({
            id: r.id, start: r.fields?.Start, eind: r.fields?.Eind,
            draaidag: r.fields?.DraaidagNaam?.[0] || r.fields?.DraaidagNaam || 'Onbekend',
        }));
    }, [reservations]);

    // Separate refresh function that doesn't depend on currentReservation
    const refreshMaterialData = useCallback(async () => {
        if (!selectedDraaidag) return;


        setIsSaving(true);

        try {
            // Fetch fresh data without reservation context
            const url = API_ENDPOINTS.draaidagData(selectedDraaidag.id);

            const response = await axios.get(url);
            const { materials, junctionData } = response.data;


            // Update materials without any reservation context
            setAlleMaterialen(materials);
            setSelectedMaterials([]); // Clear selected materials since reservation was deleted

        } catch (error) {
            console.error('Error refreshing material data:', error);
            toast.error('Fout bij verversen van materialen data');
        } finally {
            setIsSaving(false);
        }
    }, [selectedDraaidag]);

    // Track deleted reservation IDs to prevent useEffect from re-setting them
    const deletedReservationIdsRef = useRef(new Set());

    // Auto-set currentReservation when draaidag or reservations change.
    // - Never overwrite a locally-set reservation with null (race met fetchReservations)
    // - Never re-set a reservation die net verwijderd is
    // BELANGRIJK: `currentReservation` staat NIET in de deps. Anders her-triggert dit effect
    // op elke lokale (optimistische) currentReservation-update en zet het 'm direct terug uit
    // de — op dat moment nog niet ververste — reservations-array. Dat veroorzaakte de glitch
    // bij het weghalen van een +1/-1 (flag klapt terug → "lijkt niet opgeslagen"). Het effect
    // draait nu alleen op échte server-refreshes (reservations) of draaidag-wisseling.
    useEffect(() => {
        if (selectedDraaidag?.__standalone) return; // losse reservering beheert currentReservation zelf
        if (selectedDraaidag && Array.isArray(reservations)) {
            const selId = selectedDraaidag.id;
            const found = reservations.find(r =>
                Array.isArray(r?.fields?.IDdraaidagOutlook) && r.fields.IDdraaidagOutlook.includes(selId)
            );
            if (found && !deletedReservationIdsRef.current.has(found.id)) {
                setCurrentReservation(found);
            } else if (!currentReservationRef.current) {
                setCurrentReservation(null);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDraaidag?.id, reservations, setCurrentReservation]);

    // Single load trigger: wait for currentReservation to be resolved, then load once
    // Losse reservering heeft géén draaidag-id (null) → gebruik een aparte load-key,
    // anders matcht null === ref-initial(null) en laadt de pool nooit.
    const hasLoadedForDraaidagRef = useRef(null);
    const loadKey = selectedDraaidag?.__standalone
        ? `standalone:${selectedDraaidag.reservationId || 'new'}`
        : selectedDraaidag?.id;
    useEffect(() => {
        if (!selectedDraaidag) return;
        // Only load once per draaidag (after reservation is resolved)
        if (hasLoadedForDraaidagRef.current === loadKey) return;
        // Wait until reservations are loaded (currentReservation is resolved)
        if (!Array.isArray(reservations) || reservations.length === undefined) return;

        hasLoadedForDraaidagRef.current = loadKey;
        setIsDataLoaded(false);
        loadAllDataParallel();
    }, [loadKey, selectedDraaidag, currentReservation, reservations, loadAllDataParallel]);

    // Reload when reservation changes (e.g., after first save creates a new one)
    const prevReservationIdRef = useRef(null);
    useEffect(() => {
        const newId = currentReservation?.id;
        if (newId && newId !== prevReservationIdRef.current && (hasLoadedForDraaidagRef.current === loadKey || selectedDraaidag?.__standalone)) {
            prevReservationIdRef.current = newId;
            // Reservation just linked — reload with reservationId for junction data
            loadAllDataParallel(false);
        }
        if (!newId) prevReservationIdRef.current = null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentReservation?.id]);

    // Slepen-handoff: losse reservering geopend vanuit een drag → open de verplaats-modus
    // met de nieuwe datum klaargezet, zodat de gebruiker bevestigt (en conflicten oplost).
    useEffect(() => {
        const mt = selectedDraaidag?.__standalone ? selectedDraaidag.__moveTo : null;
        if (mt && currentReservation?.id && moveToHandledRef.current !== currentReservation.id) {
            moveToHandledRef.current = currentReservation.id;
            setMoveStart(mt.start);
            setMoveEnd(mt.end || mt.start);
            setMoveMode(true);
        }
    }, [selectedDraaidag, currentReservation?.id]);

    // Fetch materiaalsets + types on mount
    useEffect(() => {
        fetchMateriaalsets();
        fetchMateriaalTypes();
    }, [fetchMateriaalsets, fetchMateriaalTypes]);

    // Focus + polling refresh: ververs materialen + junctions voor de geopende draaidag
    // zodat een wijziging door een collega in een ander window zichtbaar wordt.
    // Wordt overgeslagen wanneer de gebruiker zelf bezig is, anders worden lokale
    // selecties / onopgeslagen wijzigingen door de server-state overschreven.
    const loadAllDataParallelRef = useRef(loadAllDataParallel);
    loadAllDataParallelRef.current = loadAllDataParallel;
    const isUserBusyRef = useRef(false);
    isUserBusyRef.current = (
        openWizard || isSaving || isEditingNaam ||
        copyDialogOpen || copyToDialogOpen || saveAsSetDialogOpen || deleteDialogOpen ||
        notitieStatus === 'saving' ||
        notitie !== (lastSavedValue.current || '') // onopgeslagen notitie-edits
    );
    useEffect(() => {
        if (!selectedDraaidag?.id) return;
        const refresh = () => {
            if (isUserBusyRef.current) return;
            loadAllDataParallelRef.current?.(false);
        };
        const handleVisibility = () => {
            if (document.visibilityState === 'visible') refresh();
        };
        document.addEventListener('visibilitychange', handleVisibility);
        window.addEventListener('focus', handleVisibility);
        const intervalId = setInterval(() => {
            if (document.visibilityState === 'visible') refresh();
        }, 20000);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibility);
            window.removeEventListener('focus', handleVisibility);
            clearInterval(intervalId);
        };
    }, [selectedDraaidag?.id]);

    // Optimistic UI component
    const DraaidagPreview = () => {
        if (!optimisticDraaidagData) return null;

        const d = optimisticDraaidagData;
        const callsheetUrl = d.callsheetPdfUrl || (d.callsheetRecordId ? `https://airtable.com/${AIRTABLE_BASE_ID}/${AIRTABLE_CALLSHEET_PAGE}/${d.callsheetRecordId}` : null);
        const projectUrl = d.projectRecordId ? `https://airtable.com/${AIRTABLE_BASE_ID}/${AIRTABLE_PROJECT_PAGE}/${d.projectRecordId}` : null;
        const fmtDate = (dateStr) => {
            if (!dateStr) return '';
            try { return new Date(dateStr + 'T12:00:00').toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'long' }); }
            catch { return dateStr; }
        };

        // Voortgang (gepakt) over alle gereserveerde materialen
        const mats = selectedMaterials || [];
        const totalMat = mats.length;
        const gepaktMat = mats.filter(m => (m.fields?.JunctionStatus || []).includes('Gepakt')).length;
        const pct = totalMat ? Math.round((gepaktMat / totalMat) * 100) : 0;
        const allPacked = totalMat > 0 && gepaktMat === totalMat;

        return (
            <Box>
                {/* Title bar */}
                <Box sx={{ px: { xs: 2, sm: 3.5 }, pt: { xs: 2, sm: 3 }, pb: 2.5 }}>
                    {isEditingNaam ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                            <TextField size="small" value={editWeergaveNaam} onChange={(e) => setEditWeergaveNaam(e.target.value)}
                                placeholder={d.title} label="Weergavenaam" fullWidth autoFocus
                                onKeyPress={(e) => e.key === 'Enter' && handleSaveWeergaveNaam()}
                                onBlur={handleSaveWeergaveNaam}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '0.5rem', bgcolor: T.surfaceLow, fontFamily: T.fontBody } }}
                            />
                            <Box onClick={handleSaveWeergaveNaam} sx={{ width: 32, height: 32, borderRadius: '0.5rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', '&:hover': { bgcolor: T.primaryContainer } }}>
                                <Icon name="check" size={20} sx={{ color: T.primary }} />
                            </Box>
                            <Box onClick={() => { setIsEditingNaam(false); setEditWeergaveNaam(currentReservation?.fields?.WeergaveNaam || ''); }}
                                sx={{ width: 32, height: 32, borderRadius: '0.5rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', '&:hover': { bgcolor: T.surfaceHigh } }}>
                                <Icon name="close" size={20} sx={{ color: T.outline }} />
                            </Box>
                        </Stack>
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.62rem', fontWeight: 800, color: T.outline, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.35 }}>
                                    Draaidag
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                    <Typography sx={{ fontFamily: T.font, fontWeight: 800, fontSize: { xs: '1.15rem', sm: '1.3rem' }, color: T.text, lineHeight: 1.2 }}>
                                        {d.displayTitle}
                                    </Typography>
                                    {selectedDraaidag && (
                                        <Box onClick={() => setIsEditingNaam(true)} sx={{ width: 30, height: 30, borderRadius: '0.35rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.15, '&:hover': { bgcolor: T.primaryContainer } }}>
                                            <Icon name="edit" size={15} sx={{ color: T.outlineVariant }} />
                                        </Box>
                                    )}
                                </Box>
                                {d.weergaveNaam && (
                                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.7rem', color: T.outlineVariant, mt: 0.5 }}>
                                        Origineel: {d.title}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    )}

                    {/* Meta-strip: datum / crew / projecttype — gegroepeerd met scheidslijnen */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: `repeat(${1 + (d.crew ? 1 : 0) + (d.projectType && d.projectType !== 'Onbekend' ? 1 : 0)}, minmax(0, 1fr))` }, mt: 2.5, borderRadius: '0.75rem', bgcolor: T.surfaceLow, border: `1px solid ${T.surfaceHigh}`, overflow: 'hidden' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.85, px: 1.75, py: 1.35 }}>
                            <Icon name="calendar_today" size={18} sx={{ color: T.primary, flexShrink: 0 }} />
                            <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.6rem', fontWeight: 800, color: T.outline, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Datum</Typography>
                                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.88rem', fontWeight: 700, color: T.text, lineHeight: 1.3 }}>
                                    {fmtDate(d.startDate)}{d.endDate !== d.startDate && ` – ${fmtDate(d.endDate)}`}
                                    {d.aantalDagen && <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: '0.74rem', fontWeight: 500, color: T.outline, ml: 0.5 }}>({d.aantalDagen})</Typography>}
                                </Typography>
                            </Box>
                        </Box>
                        {d.crew && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.85, px: 1.75, py: 1.35, borderTop: { xs: `1px solid ${T.surfaceHigh}`, sm: 'none' }, borderLeft: { sm: `1px solid ${T.surfaceHigh}` } }}>
                                <Icon name="groups" size={18} sx={{ color: T.primary, flexShrink: 0 }} />
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.6rem', fontWeight: 800, color: T.outline, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Crew</Typography>
                                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.88rem', fontWeight: 700, color: T.text, lineHeight: 1.3 }}>{d.crew}</Typography>
                                </Box>
                            </Box>
                        )}
                        {d.projectType && d.projectType !== 'Onbekend' && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.85, px: 1.75, py: 1.35, borderTop: { xs: `1px solid ${T.surfaceHigh}`, sm: 'none' }, borderLeft: { sm: `1px solid ${T.surfaceHigh}` } }}>
                                <Icon name="movie" size={18} sx={{ color: T.primary, flexShrink: 0 }} />
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.6rem', fontWeight: 800, color: T.outline, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Projecttype</Typography>
                                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.88rem', fontWeight: 700, color: T.text, lineHeight: 1.3 }}>{d.projectType}</Typography>
                                </Box>
                            </Box>
                        )}
                    </Box>

                    {/* Links */}
                    <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                        {d.klantAccountId && (
                            <Box component="a" href={`/accounts/${d.klantAccountId}/workspace`}
                                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.4, py: 0.8, borderRadius: '0.5rem', border: `1px solid ${T.surfaceHigh}`, bgcolor: T.surface, textDecoration: 'none', transition: 'all 0.15s', '&:hover': { bgcolor: T.primaryContainer, borderColor: 'transparent' } }}>
                                {accountInfo?.beeldmerkUrl ? (
                                    <img src={accountInfo.beeldmerkUrl} alt="" width={16} height={16} style={{ flexShrink: 0, borderRadius: 2, objectFit: 'contain' }} />
                                ) : (
                                    <Icon name="assured_workload" size={16} sx={{ color: T.primary }} />
                                )}
                                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', fontWeight: 600, color: T.primary }}>
                                    {accountInfo?.naam || d.klantNaam || 'Klant'}
                                </Typography>
                            </Box>
                        )}
                        {projectUrl && (
                            <Box component="a" href={projectUrl} target="_blank" rel="noopener noreferrer"
                                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.4, py: 0.8, borderRadius: '0.5rem', border: `1px solid ${T.surfaceHigh}`, bgcolor: T.surface, textDecoration: 'none', transition: 'all 0.15s', '&:hover': { bgcolor: T.primaryContainer, borderColor: 'transparent' } }}>
                                <img src="https://airtable.com/favicon.ico" alt="" width={16} height={16} style={{ flexShrink: 0 }} />
                                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', fontWeight: 600, color: T.primary }}>
                                    [{d.projectNummer}] {d.projectNaam}
                                </Typography>
                                <Icon name="open_in_new" size={12} sx={{ color: T.outlineVariant }} />
                            </Box>
                        )}
                        {callsheetUrl ? (
                            <Box component="a" href={callsheetUrl} target="_blank" rel="noopener noreferrer"
                                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.4, py: 0.8, borderRadius: '0.5rem', border: `1px solid ${T.surfaceHigh}`, bgcolor: T.surface, textDecoration: 'none', transition: 'all 0.15s', '&:hover': { bgcolor: T.tertiaryContainer, borderColor: 'transparent' } }}>
                                <Icon name="description" size={16} sx={{ color: T.tertiary }} />
                                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', fontWeight: 600, color: T.tertiary }}>
                                    Callsheet
                                </Typography>
                                <Icon name="open_in_new" size={12} sx={{ color: T.outlineVariant }} />
                            </Box>
                        ) : (
                            <Box
                                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.4, py: 0.8, borderRadius: '0.5rem', border: `1px dashed ${T.surfaceHigh}`, bgcolor: 'transparent', cursor: 'default' }}>
                                <Icon name="description" size={16} sx={{ color: T.outlineVariant }} />
                                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', fontWeight: 600, color: T.outline }}>
                                    Callsheet
                                </Typography>
                                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', fontWeight: 500, color: T.outlineVariant }}>
                                    (Niet beschikbaar)
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* Omschrijving — onderaan, preview + uitklap */}
                    {d.description && (
                        <Box sx={{ mt: 1.75, pt: 1.5, borderTop: `1px solid ${T.surfaceHigh}` }}>
                            <Box onClick={() => setDescExpanded(v => !v)} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer', '&:hover .desc-label': { color: T.text } }}>
                                <Icon name="notes" size={16} sx={{ color: T.outline, flexShrink: 0 }} />
                                <Typography className="desc-label" sx={{ fontFamily: T.fontBody, fontSize: '0.62rem', fontWeight: 800, color: T.outline, textTransform: 'uppercase', letterSpacing: '0.06em', flexGrow: 1, transition: 'color 0.15s' }}>
                                    Omschrijving
                                </Typography>
                                <Icon name={descExpanded ? 'expand_less' : 'expand_more'} size={20} sx={{ color: T.outline, flexShrink: 0 }} />
                            </Box>
                            {descExpanded ? (
                                <Collapse in={descExpanded}>
                                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.9rem', color: T.text, lineHeight: 1.55, whiteSpace: 'pre-wrap', mt: 1 }}>
                                        {d.description}
                                    </Typography>
                                    <Typography onClick={() => setDescExpanded(false)} sx={{ fontFamily: T.fontBody, fontSize: '0.7rem', fontWeight: 700, color: T.primary, cursor: 'pointer', mt: 0.5, '&:hover': { textDecoration: 'underline' } }}>
                                        Toon minder
                                    </Typography>
                                </Collapse>
                            ) : (
                                <Box onClick={() => setDescExpanded(true)} sx={{ cursor: 'pointer', mt: 0.75 }}>
                                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: T.secondary, lineHeight: 1.45, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                        {d.description}
                                    </Typography>
                                    <Typography className="desc-label" sx={{ fontFamily: T.fontBody, fontSize: '0.7rem', fontWeight: 700, color: T.primary, mt: 0.25 }}>
                                        Lees meer…
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}

                </Box>
            </Box>
        );
    };

    // Process reservations data for availability checking
    const processReservationsData = useCallback((reservations) => {
            const reservationsData = {};

        if (Array.isArray(reservations)) {
            reservations.forEach(record => {
                if (record.fields && record.fields.Materiaal && Array.isArray(record.fields.Materiaal)) {
                    // Get the date range for this reservation
                    const startDate = new Date(record.fields.Start);
                    const endDate = new Date(record.fields.Eind);
                    const draaidagNaam = record.fields.DraaidagNaam?.[0] || record.fields.DraaidagNaamOutlook?.[0] || 'Onbekende draaidag';

                    record.fields.Materiaal.forEach(materialId => {
                        if (!reservationsData[materialId]) {
                            reservationsData[materialId] = [];
                        }

                        // Store reservation info with draaidag name and extra dag flags
                        reservationsData[materialId].push({
                            startDate: startDate.toISOString(),
                            endDate: endDate.toISOString(),
                            draaidagNaam: draaidagNaam,
                            reservationId: record.id,
                            extraDagVoor: record.fields.ExtraDagVoor || false,
                            extraDagNa: record.fields.ExtraDagNa || false,
                            draaidagStart: record.fields.startDatumDraaidag || null,
                            draaidagEnd: record.fields.eindDatumDraaidag || null,
                        });
                    });
                }
            });
        }

        return reservationsData;
    }, []);

    // Add updateJunctionStatus function
    const updateJunctionStatus = useCallback(async (junctionId, statuses) => {
        if (!junctionId) {
            console.error('JunctionID is undefined - cannot update status');
            toast.error('Kan status niet updaten - JunctionID ontbreekt. Probeer de pagina te verversen.');
            return { ok: false, error: 'JunctionID is undefined' };
        }

        try {
            const response = await axios.patch(API_ENDPOINTS.junctions.update(junctionId), {
                statuses: Array.isArray(statuses) ? statuses : [statuses]
            });

            return { ok: true, data: response.data };
        } catch (error) {
            console.error('Error updating junction status:', error);
            if (error.response) {
                console.error('Response data:', error.response.data);
                console.error('Response status:', error.response.status);
            }
            toast.error('Fout bij het updaten van de status');
            return { ok: false, error };
        }
    }, []);

    // Add updateJunctionStatus function with legacy support
    const updateJunctionStatusWithLegacySupport = useCallback(async (material, statuses) => {
        // Legacy/geen junction: altijd via backend een echte junction zoeken of maken
        const existingUrl = API_ENDPOINTS.junctions.existing();
        const createUrl = API_ENDPOINTS.junctions.create();
        const statusesArray = Array.isArray(statuses) ? statuses : [statuses];
        const isSynthetic = !material.fields?.JunctionID || String(material.fields?.JunctionID).startsWith('synthetic_');
        if (isSynthetic) {
            try {
                // Check bestaande junction
                const existingResp = await axios.get(existingUrl, {
                    params: { reservationId: currentReservation?.id, materialId: material.id }
                });
                let junctionId = existingResp.data?.[0]?.id;
                if (!junctionId) {
                    // Maak echte junction aan in oude base
                    const createResp = await axios.post(createUrl, {
                        reservationId: currentReservation?.id,
                        materialId: material.id,
                        statuses: statusesArray,
                        draaidagId: selectedDraaidag?.id
                    });
                    junctionId = createResp.data?.records?.[0]?.id || createResp.data?.id;
                } else {
                    // Update bestaande junction
                    await axios.patch(API_ENDPOINTS.junctions.update(junctionId), { statuses: statusesArray });
                }

                const updatedMaterial = {
                    ...material,
                    fields: {
                        ...material.fields,
                        JunctionID: junctionId,
                        JunctionStatus: statusesArray
                    }
                };
                setSelectedMaterials(prev => prev.map(m => m.id === material.id ? updatedMaterial : m));
                setAlleMaterialen(prev => prev.map(m => m.id === material.id ? updatedMaterial : m));
                toast.success('Status bijgewerkt');
                return { ok: true };
            } catch (e) {
                console.error('Backend junction create/update failed:', e.response?.data || e.message);
                toast.error('Fout bij updaten van status');
                return { ok: false, error: e };
            }
        }

        // Normal junction update for non-legacy materials
        try {
            const response = await axios.patch(API_ENDPOINTS.junctions.update(material.fields.JunctionID), {
                statuses: statusesArray
            });

            // Optimistisch bijwerken zodat de algemene voortgangsteller direct meebeweegt
            const updatedMaterial = {
                ...material,
                fields: { ...material.fields, JunctionStatus: statusesArray }
            };
            setSelectedMaterials(prev => prev.map(m => m.id === material.id ? updatedMaterial : m));
            setAlleMaterialen(prev => prev.map(m => m.id === material.id ? updatedMaterial : m));

            return { ok: true, data: response.data };
        } catch (error) {
            console.error('Error updating junction status:', error);
            toast.error('Fout bij het updaten van de status');
            return { ok: false, error };
        }
    }, [currentReservation, selectedDraaidag, updateJunctionStatus]);

    const handleSave = useCallback(async (newlySelectedMaterials, startDate, endDate) => {
        // Zorg ervoor dat we een array hebben
        const materialsArray = Array.isArray(newlySelectedMaterials) ? newlySelectedMaterials : [];
        const newMaterialIds = materialsArray.map(m => m.id);

        setIsSaving(true);

        // Optimistic update: update UI immediately
        setSelectedMaterials(materialsArray);
        if (!selectedDraaidag.__standalone && typeof updateDraaidagLocally === 'function') {
            updateDraaidagLocally({ id: selectedDraaidag.id, fields: { AantalMateriaalGereserveerd: newMaterialIds.length } });
        }

        try {
            const response = await axios.post(API_ENDPOINTS.reservations.save(), {
                reservationId: currentReservation?.id,
                draaidagId: selectedDraaidag.__standalone ? null : selectedDraaidag.id,
                reserveringNaam: selectedDraaidag.__standalone ? (selectedDraaidag.fields?.Draaidag || 'Losse reservering') : undefined,
                materialIds: newMaterialIds,
                startDate: startDate || selectedDraaidag.fields.Start || selectedDraaidag.fields.StartDatum,
                endDate: endDate || selectedDraaidag.fields.Eind || selectedDraaidag.fields.EindDatum,
                extraDagNa: extraDagNa,
                extraDagVoor: extraDagVoor
            });

            const extraDagenMsg = (extraDagVoor && extraDagNa) ? " (met extra dagen voor en na)" :
                                  extraDagVoor ? " (met extra dag voor)" :
                                  extraDagNa ? " (met extra dag na)" : "";
            toast.success(`Reservering opgeslagen${extraDagenMsg}!`);

            // Calculate actual dates for local state (same logic as backend)
            let actualStartDate = startDate || selectedDraaidag.fields.Start || selectedDraaidag.fields.StartDatum;
            let actualEndDate = endDate || selectedDraaidag.fields.Eind || selectedDraaidag.fields.EindDatum;

            if (extraDagVoor && actualStartDate) {
                const start = new Date(actualStartDate);
                start.setDate(start.getDate() - 1);
                actualStartDate = start.toISOString().split('T')[0];
            }
            if (extraDagNa && actualEndDate) {
                const end = new Date(actualEndDate);
                end.setDate(end.getDate() + 1);
                actualEndDate = end.toISOString().split('T')[0];
            }

            // Als er geen reservering was, hebben we er nu wel een
            if (!currentReservation && response.data.reservationId) {
                const newReservationMemo = {
                    id: response.data.reservationId,
                    fields: {
                        Start: actualStartDate,
                        Eind: actualEndDate,
                        Materiaal: newMaterialIds,
                        ExtraDagNa: extraDagNa,
                        ExtraDagVoor: extraDagVoor
                    }
                };
                setCurrentReservation(newReservationMemo);
                // Losse reservering: koppel de virtuele draaidag aan de nieuwe reservering-id,
                // zodat reloads/saves dezelfde reservering blijven targeten (geen duplicaten,
                // materialen verdwijnen niet meer).
                if (selectedDraaidag.__standalone && !selectedDraaidag.reservationId) {
                    setSelectedDraaidag({ ...selectedDraaidag, reservationId: response.data.reservationId });
                }
            } else if (currentReservation && response.data.reservationId) {
                // Update existing reservation with new material data
                const updatedReservation = {
                    ...currentReservation,
                    fields: {
                        ...currentReservation.fields,
                        Materiaal: newMaterialIds,
                        Start: actualStartDate,
                        Eind: actualEndDate,
                        ExtraDagNa: extraDagNa,
                        ExtraDagVoor: extraDagVoor
                    }
                };
                setCurrentReservation(updatedReservation);
            }

            // Immediate refresh of junction data for newly added materials
            try {
                const reservationId = currentReservation?.id || response.data.reservationId;
                if (reservationId && selectedDraaidag.__standalone) {
                    // Losse reservering: junctions direct via de junctions-API (geen draaidag-data)
                    const jResp = await axios.get(API_ENDPOINTS.junctions.list(), { params: { reservationId } });
                    const newJunctionData = Array.isArray(jResp.data) ? jResp.data : [];
                    const withJunctions = materialsArray.map(material => {
                        const junction = newJunctionData.find(j => j.fields?.Materiaal?.[0] === material.id);
                        return junction
                            ? { ...material, fields: { ...material.fields, JunctionID: junction.id, JunctionStatus: junction.fields.StatusJunction || [] } }
                            : material;
                    });
                    setSelectedMaterials(withJunctions);
                } else if (reservationId && !selectedDraaidag.__standalone) {
                    const junctionResponse = await axios.get(
                        `${API_ENDPOINTS.draaidagData(selectedDraaidag.id)}?reservationId=${reservationId}`
                    );
                    const { junctionData: newJunctionData } = junctionResponse.data;

                    // Update selected materials with new junction data
                    const updatedMaterials = materialsArray.map(material => {
                        const junction = newJunctionData.find(j => j.fields?.Materiaal?.[0] === material.id);
                        if (junction) {
                            return {
                                ...material,
                                fields: {
                                    ...material.fields,
                                    JunctionID: junction.id,
                                    JunctionStatus: junction.fields.StatusJunction || []
                                }
                            };
                        }
                        return material;
                    });

                    setSelectedMaterials(updatedMaterials);

                    // Also update alleMaterialen with junction data for consistency
                    const updatedAlleMaterialen = alleMaterialen.map(material => {
                        const junction = newJunctionData.find(j => j.fields?.Materiaal?.[0] === material.id);
                        if (junction) {
                            return {
                                ...material,
                                fields: {
                                    ...material.fields,
                                    JunctionID: junction.id,
                                    JunctionStatus: junction.fields.StatusJunction || []
                                }
                            };
                        }
                        return material;
                    });
                    setAlleMaterialen(updatedAlleMaterialen);
                }
            } catch (error) {
                console.error('Error refreshing junction data immediately:', error);
            }

            // Background sync voor data consistentie (niet blocking)
            // Dit zorgt ervoor dat andere gebruikers de wijzigingen zien
            setTimeout(() => {
                fetchReservations(true); // true = force refresh, bypass cache
            }, 2000); // 2 seconde delay - genoeg tijd voor gebruiker om door te gaan

            if (typeof fetchDraaidagenFromBackend === 'function') {
                setTimeout(() => {
                    fetchDraaidagenFromBackend();
                }, 2000);
            }

            // Immediate refresh of reservations to ensure data consistency
            setTimeout(() => {
                fetchReservations(true); // true = force refresh
            }, 500); // 500ms delay to ensure backend has processed the save

            setOpenWizard(false);
        } catch (err) {
            console.error('Save error:', err);

            // Revert optimistic updates on error
            if (currentReservation?.fields?.Materiaal) {
                // Revert to original materials if there was a previous reservation
                const originalMaterialIds = currentReservation.fields.Materiaal || [];
                const originalMaterials = alleMaterialen.filter(m => originalMaterialIds.includes(m.id));
                setSelectedMaterials(originalMaterials);
            } else {
                // Clear selection if this was a new reservation
                setSelectedMaterials([]);
            }

            if (typeof updateDraaidagLocally === 'function') {
                // Revert draaidag count
                const originalCount = currentReservation?.fields?.Materiaal?.length || 0;
                updateDraaidagLocally({ id: selectedDraaidag.id, fields: { AantalMateriaalGereserveerd: originalCount } });
            }

            if (err.response) {
                console.error('Response data:', err.response.data);
                console.error('Response status:', err.response.status);

                if (err.response.status === 409) {
                    // Conflict - materials were reserved by another user
                    toast.error(err.response.data.error?.message || 'Materialen zijn net gereserveerd door een andere gebruiker');
                    // Force refresh to show current availability
                    await fetchReservations(true);
                    const response = await axios.get(API_ENDPOINTS.materials.list());
                    setAlleMaterialen(response.data);
                } else {
                    toast.error(`Opslaan mislukt: ${err.response.data.error?.message || 'Onbekende fout'}`);
                }
        } else {
                toast.error("Opslaan mislukt.");
            }
        } finally {
            setIsSaving(false);
        }
    }, [currentReservation, selectedDraaidag, fetchReservations, updateDraaidagLocally, setSelectedDraaidag, setCurrentReservation, alleMaterialen, fetchDraaidagenFromBackend, extraDagNa]);

    const handleGoBack = useCallback(() => setSelectedDraaidag(null), [setSelectedDraaidag]);
    const handleOpenWizard = useCallback(() => {
        setSelectedCategoryIndex(null); // Reset category when opening normally
        setOpenWizard(true);
    }, []);
    const handleCloseWizard = useCallback(() => {
        setOpenWizard(false);
        // Delay resetting category to prevent flicker
        setTimeout(() => {
            setSelectedCategoryIndex(null);
        }, 300);
    }, []);

    // Sync notitie als reservation wisselt
    useEffect(() => {
        const notitieValue = currentReservation?.fields?.Notities || '';

        // Niet overschrijven tijdens opslaan, of als er onopgeslagen lokale wijzigingen zijn
        // (anders verdwijnt de notitie-in-bewerking bij een achtergrond-refresh / polling).
        const isDirty = notitie !== (lastSavedValue.current || '');
        if (notitieStatus === 'saving' || isDirty) {
            return;
        }

        if (notitie !== notitieValue) {
            setNotitie(notitieValue);
            lastSavedValue.current = notitieValue;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentReservation]);

    // Sync extraDagNa en extraDagVoor als reservation wisselt
    useEffect(() => {
        const extraDagNaValue = currentReservation?.fields?.ExtraDagNa === true;
        const extraDagVoorValue = currentReservation?.fields?.ExtraDagVoor === true;
        setExtraDagNa(extraDagNaValue);
        setExtraDagVoor(extraDagVoorValue);
    }, [currentReservation]);

    // PATCH notitie naar Airtable - manual save
    const saveNotitie = useCallback(async (value) => {
        if (!currentReservation?.id) return;

        setNotitieStatus('saving');

        try {
            await axios.patch(API_ENDPOINTS.reservations.updateNotitie(currentReservation.id), {
                notitie: value
            });

            setNotitieStatus('saved');
            lastSavedValue.current = value;
            setTimeout(() => setNotitieStatus(''), 800);

            // Manually update the reservation in the context
            updateReservationField(currentReservation.id, 'Notities', value);

        } catch (e) {
            setNotitieStatus('error');
            setTimeout(() => setNotitieStatus(''), 1500);
            toast.error('Fout bij opslaan notitie');
        }
    }, [currentReservation, updateReservationField]);

    // Manual save handlers
    const handleNotitieSave = useCallback(() => {
        if (notitie !== (currentReservation?.fields?.Notities || '')) {
            saveNotitie(notitie);
        }
    }, [notitie, saveNotitie, currentReservation]);

    const handleNotitieKeyPress = useCallback((e) => {
        if (e.key === 'Enter' && e.ctrlKey) {
            e.preventDefault();
            handleNotitieSave();
        }
    }, [handleNotitieSave]);

    // Add delete reservation functionality
    const handleDeleteReservation = useCallback(async (reservationId) => {
        if (!reservationId) return;

        try {

            // Use backend endpoint instead of direct Airtable API calls
            const response = await axios.delete(API_ENDPOINTS.reservations.delete(reservationId));

            if (response.status === 200) {
                toast.success("Reservering succesvol verwijderd!");

                // Mark as deleted so useEffect won't re-set it from stale data
                deletedReservationIdsRef.current.add(reservationId);

                // Clear all local state
                setCurrentReservation(null);
                setSelectedMaterials([]);
                setAlleMaterialen([]);
                setNotitie('');

                // Refresh all data
                await fetchReservations(true);

                // Losse reservering: geen draaidag om op te blijven → terug naar de kalender
                if (selectedDraaidag?.__standalone) {
                    setSelectedDraaidag(null);
                } else if (selectedDraaidag) {
                    // Bij een draaidag: herlaad de materiaaldata, blijf op de pagina
                    await refreshMaterialData();
                }
            } else {
                toast.error('Fout bij verwijderen van de reservering');
            }
        } catch (error) {
            console.error("Error during delete operation: ", error);
            if (error.response) {
                console.error('Response data:', error.response.data);
                console.error('Response status:', error.response.status);
                toast.error(`Fout bij verwijderen van reservering: ${error.response.data?.error || 'Onbekende fout'}`);
            } else {
                toast.error("Fout bij verwijderen van reservering");
            }
        }
    }, [fetchReservations, setSelectedDraaidag, setCurrentReservation, selectedDraaidag, refreshMaterialData]);

    // Functie om de "Geen Materiaal Nodig" checkbox te behandelen
    // Dit is een draaidag-eigenschap, geen reservering — update direct op de draaidag in Airtable
    const handleGeenMateriaalNodigChange = async (event) => {
        const isChecked = event?.target?.checked !== undefined ? event.target.checked : !geenMateriaalNodig;
        setGeenMateriaalNodig(isChecked);
        setIsSaving(true);

        try {
            // Update draaidag in Airtable
            await axios.patch(`${BACKEND_URL}/Api/draaidagen/${selectedDraaidag.id}`, {
                fields: { 'Geen Materiaal Nodig': isChecked }
            });

            // Update draaidag locally for calendar color + current session
            const updatedDraaidag = {
                ...selectedDraaidag,
                fields: { ...selectedDraaidag.fields, 'Geen Materiaal Nodig': isChecked }
            };
            if (updateDraaidagLocally) updateDraaidagLocally(updatedDraaidag);
            setSelectedDraaidag(updatedDraaidag);

            toast.success(isChecked ? 'Geen materiaal nodig' : 'Materiaal weer nodig');
        } catch (error) {
            console.error('Fout bij "Geen materiaal nodig":', error);
            toast.error('Fout bij opslaan.');
            setGeenMateriaalNodig(!isChecked);
        } finally {
            setIsSaving(false);
        }
    };

    // Bouw clash-groepen: combineer de autoritatieve unavailable-lijst (check-availability)
    // met de client-side draaidag/datum-details uit processReservationsData.
    const buildClashGroups = (unavailableMaterials, extStart, extEnd) => {
        const resData = processReservationsData(reservations);
        const extS = new Date(extStart);
        const extE = new Date(extEnd);
        return (unavailableMaterials || []).map(u => {
            const material = alleMaterialen.find(m => m.id === u.id) || u;
            const all = resData[u.id] || [];
            const conflicts = all.filter(r =>
                r.reservationId !== currentReservation?.id &&
                new Date(r.startDate) <= extE && new Date(r.endDate) >= extS
            );
            return { material, conflicts };
        });
    };

    // Happy-path persist (geen clash, of alleen-goedkeuren): PATCH datum + flag.
    const persistExtraDagPatch = async ({ direction, isChecked, newStartDate, newEndDate }) => {
        const isNa = direction === 'na';
        const fields = isNa
            ? { ExtraDagNa: isChecked, Eind: newEndDate }
            : { ExtraDagVoor: isChecked, Start: newStartDate };
        await axios.patch(API_ENDPOINTS.reservations.update(currentReservation.id), { fields });
        setCurrentReservation({
            ...currentReservation,
            fields: { ...currentReservation.fields, ...fields },
        });
        await fetchReservations(true);
    };

    const extraDagToast = (direction, isChecked) =>
        direction === 'na'
            ? (isChecked ? 'Extra dag toegevoegd aan reservering!' : 'Extra dag verwijderd van reservering.')
            : (isChecked ? 'Extra dag voor draaidag toegevoegd!' : 'Extra dag voor draaidag verwijderd.');

    // Gedeelde handler voor +1 ('na') en -1 ('voor') verlenging.
    const applyExtraDag = async (direction) => {
        const isNa = direction === 'na';
        const setFlag = isNa ? setExtraDagNa : setExtraDagVoor;
        const currentFlag = isNa ? extraDagNa : extraDagVoor;
        const isChecked = !currentFlag;

        // Alleen beschikbaar als er een reservering is
        if (!currentReservation?.id) {
            setFlag(isChecked); // Alleen lokale state; wordt opgeslagen bij eerste reservering
            return;
        }

        setFlag(isChecked); // Optimistic update
        setIsSaving(true);

        try {
            // Basis = de DRAAIDAG-datums (zonder buffer), NIET de reservering-datums.
            // De reservering-datums bevatten al een eventuele buffer; als je die als basis
            // neemt, krimpt de datum niet meer wanneer je een +1/-1 weer verwijdert →
            // de extra dag blijft in PostgreSQL staan en veroorzaakt een spook-conflict.
            const coreStart = selectedDraaidag?.fields?.Start || selectedDraaidag?.fields?.StartDatum || currentReservation?.fields?.Start;
            const coreEnd = selectedDraaidag?.fields?.Eind || selectedDraaidag?.fields?.EindDatum || currentReservation?.fields?.Eind;
            const toDayStr = (d) => new Date(d).toISOString().split('T')[0];
            const shiftDay = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x.toISOString().split('T')[0]; };

            // Resulterende buffer-state na deze toggle (de andere kant blijft ongewijzigd)
            const resultingVoor = isNa ? extraDagVoor : isChecked;
            const resultingNa = isNa ? isChecked : extraDagNa;

            const newStartDate = coreStart ? (resultingVoor ? shiftDay(coreStart, -1) : toDayStr(coreStart)) : null;
            const newEndDate = coreEnd ? (resultingNa ? shiftDay(coreEnd, 1) : toDayStr(coreEnd)) : null;

            // Effectieve (verlengde) periode voor de availability-check
            const effStart = newStartDate;
            const effEnd = newEndDate;

            const materialIds = selectedMaterials?.length
                ? selectedMaterials.map(m => m.id)
                : (currentReservation?.fields?.Materiaal || []);

            // Beschikbaarheidscheck alleen bij TOEVOEGEN (isChecked): de periode wordt dan
            // groter en kan een conflict opleveren. Bij VERWIJDEREN krimpt de periode →
            // nooit een nieuw conflict → check overslaan (sneller, geen onterechte clash-popup).
            if (isChecked && materialIds.length > 0 && effStart && effEnd) {
                const availability = await axios.post(API_ENDPOINTS.reservations.checkAvailability(), {
                    materialIds,
                    startDate: effStart,
                    endDate: effEnd,
                    currentReservationId: currentReservation.id
                });
                if (availability.data?.unavailable?.length > 0) {
                    // Clash → open goedkeurings-dialog i.p.v. te reverten
                    setPendingExtension({ direction, isChecked, baseStart: coreStart, baseEnd: coreEnd, newStartDate, newEndDate, effStart, effEnd, materialIds });
                    setClashGroups(buildClashGroups(availability.data.unavailable, effStart, effEnd));
                    setClashDialogOpen(true);
                    setIsSaving(false);
                    return; // toggle blijft visueel "aan"; afhandeling in confirm/cancel
                }
            }

            // Geen clash → happy-path
            await persistExtraDagPatch({ direction, isChecked, newStartDate, newEndDate });
            toast.success(extraDagToast(direction, isChecked));
        } catch (error) {
            console.error(`Fout bij bijwerken "Extra dag ${isNa ? 'na' : 'voor'} draaidag":`, error);
            toast.error('Fout bij opslaan wijziging.');
            setFlag(!isChecked); // Revert optimistic update on error
        } finally {
            setIsSaving(false);
        }
    };

    const handleExtraDagNaChange = () => applyExtraDag('na');
    const handleExtraDagVoorChange = () => applyExtraDag('voor');

    // ── Losse reservering verplaatsen (datum wijzigen) ──
    // Persisteer de nieuwe datums (en evt. vervangen materialen) via save-reservation.
    const persistMove = async (newStart, newEnd, materialIds) => {
        await axios.post(API_ENDPOINTS.reservations.save(), {
            reservationId: currentReservation.id,
            draaidagId: selectedDraaidag.__standalone ? null : selectedDraaidag.id,
            reserveringNaam: selectedDraaidag.__standalone ? (selectedDraaidag.fields?.Draaidag || 'Losse reservering') : undefined,
            materialIds,
            startDate: newStart,
            endDate: newEnd,
            extraDagNa: false,
            extraDagVoor: false,
        });
        // Lokale state + header bijwerken
        setCurrentReservation(prev => prev ? { ...prev, fields: { ...prev.fields, Start: newStart, Eind: newEnd, Materiaal: materialIds } } : prev);
        setSelectedDraaidag({ ...selectedDraaidag, fields: { ...selectedDraaidag.fields, Start: newStart, Eind: newEnd, StartDatum: newStart, EindDatum: newEnd } });
        await fetchReservations(true);
    };

    // Start een verplaatsing: check beschikbaarheid op de nieuwe periode, toon clash-dialog of persisteer.
    const applyMoveReservation = async (newStart, newEnd) => {
        if (!currentReservation?.id || !newStart || !newEnd) return;
        if (new Date(newEnd) < new Date(newStart)) { toast.error('Einddatum ligt vóór de startdatum.'); return; }
        const curStart = currentReservation.fields?.Start;
        const curEnd = currentReservation.fields?.Eind;
        if (newStart === curStart && newEnd === curEnd) { setMoveMode(false); return; } // geen wijziging
        const materialIds = selectedMaterials?.length ? selectedMaterials.map(m => m.id) : (currentReservation.fields?.Materiaal || []);
        setIsSaving(true);
        try {
            if (materialIds.length > 0) {
                const availability = await axios.post(API_ENDPOINTS.reservations.checkAvailability(), {
                    materialIds, startDate: newStart, endDate: newEnd, currentReservationId: currentReservation.id,
                });
                if (availability.data?.unavailable?.length > 0) {
                    setPendingMove({ newStart, newEnd, materialIds });
                    setClashGroups(buildClashGroups(availability.data.unavailable, newStart, newEnd));
                    setClashDialogOpen(true);
                    setIsSaving(false);
                    return;
                }
            }
            await persistMove(newStart, newEnd, materialIds);
            setMoveMode(false);
            toast.success('Reservering verplaatst!');
        } catch (error) {
            console.error('Fout bij verplaatsen reservering:', error);
            toast.error('Fout bij verplaatsen van de reservering.');
        } finally {
            setIsSaving(false);
        }
    };

    // Annuleren van de clash-dialog → revert de toggle (zoals voorheen bij een clash)
    const handleClashCancel = () => {
        setClashDialogOpen(false);
        if (pendingMove) { setPendingMove(null); setClashGroups([]); return; }
        if (pendingExtension) {
            const { direction, isChecked } = pendingExtension;
            (direction === 'na' ? setExtraDagNa : setExtraDagVoor)(!isChecked);
        }
        setPendingExtension(null);
        setClashGroups([]);
    };

    // Bevestigen → goedkeuren (dubbele boeking) en/of vervangen, dan de verlenging toepassen.
    const handleClashConfirm = async (resolutions) => {
        setClashDialogOpen(false);

        // Verplaatsing van een losse reservering
        if (pendingMove) {
            const { newStart, newEnd, materialIds } = pendingMove;
            const hasReplacements = Object.values(resolutions).some(r => r.mode === 'replace' && r.replacementId);
            const finalIds = hasReplacements
                ? materialIds.map(id => { const r = resolutions[id]; return (r && r.mode === 'replace' && r.replacementId) ? r.replacementId : id; })
                : materialIds;
            setIsSaving(true);
            try {
                await persistMove(newStart, newEnd, finalIds);
                if (hasReplacements) {
                    const sm = finalIds.map(id => alleMaterialen.find(m => m.id === id)).filter(Boolean);
                    if (sm.length) setSelectedMaterials(sm);
                }
                setMoveMode(false);
                toast.success('Reservering verplaatst!');
            } catch (error) {
                console.error('Fout bij verplaatsen (clash-goedkeuring):', error);
                toast.error('Fout bij verplaatsen van de reservering.');
            } finally {
                setIsSaving(false);
                setPendingMove(null);
                setClashGroups([]);
            }
            return;
        }

        if (!pendingExtension) return;
        const { direction, isChecked, baseStart, baseEnd, newStartDate, newEndDate, materialIds } = pendingExtension;
        const isNa = direction === 'na';
        const hasReplacements = Object.values(resolutions).some(r => r.mode === 'replace' && r.replacementId);

        setIsSaving(true);
        try {
            if (!hasReplacements) {
                // Alleen goedkeuren → bestaande PATCH; dubbele boeking blijft bestaan
                await persistExtraDagPatch({ direction, isChecked, newStartDate, newEndDate });
            } else {
                // Vervangingen → swap materiaallijst en save met DRAAIDAG-BASISDATUMS + flags
                // (de save-endpoint past de extra-dag-offset zelf toe op de flags)
                const swappedIds = materialIds.map(id => {
                    const r = resolutions[id];
                    return (r && r.mode === 'replace' && r.replacementId) ? r.replacementId : id;
                });
                const draaidagBaseStart = selectedDraaidag?.fields?.Start || selectedDraaidag?.fields?.StartDatum || baseStart;
                const draaidagBaseEnd = selectedDraaidag?.fields?.Eind || selectedDraaidag?.fields?.EindDatum || baseEnd;
                await axios.post(API_ENDPOINTS.reservations.save(), {
                    reservationId: currentReservation.id,
                    draaidagId: selectedDraaidag.id,
                    materialIds: swappedIds,
                    startDate: draaidagBaseStart,
                    endDate: draaidagBaseEnd,
                    extraDagNa: isNa ? isChecked : extraDagNa,
                    extraDagVoor: isNa ? extraDagVoor : isChecked,
                });
                // Lokale state bijwerken (newStartDate/newEndDate = al-verlengde datums voor weergave)
                const swappedMaterials = swappedIds.map(id => alleMaterialen.find(m => m.id === id)).filter(Boolean);
                if (swappedMaterials.length) setSelectedMaterials(swappedMaterials);
                setCurrentReservation({
                    ...currentReservation,
                    fields: {
                        ...currentReservation.fields,
                        Materiaal: swappedIds,
                        ...(isNa ? { ExtraDagNa: isChecked, Eind: newEndDate } : { ExtraDagVoor: isChecked, Start: newStartDate }),
                    },
                });
                await fetchReservations(true);
            }
            toast.success(extraDagToast(direction, isChecked));
        } catch (error) {
            console.error('Fout bij verwerken clash-goedkeuring:', error);
            toast.error('Fout bij opslaan wijziging.');
            (isNa ? setExtraDagNa : setExtraDagVoor)(!isChecked); // revert
        } finally {
            setIsSaving(false);
            setPendingExtension(null);
            setClashGroups([]);
        }
    };

    // Note: This useEffect handles the scenario where the reservation is deleted.
    // Only clear state if we previously had a currentReservation but it is no longer present in reservations.
    useEffect(() => {
        if (selectedDraaidag && Array.isArray(reservations) && currentReservation) {
            // First try ID match
            let found = reservations.find(r => r?.fields?.IDdraaidagOutlook?.[0] === selectedDraaidag.id);

            // Fallback: date match to avoid false negatives when ID link is missing
            if (!found) {
                const selStart = selectedDraaidag.fields?.Start || selectedDraaidag.fields?.StartDatum;
                const selEind = selectedDraaidag.fields?.Eind || selectedDraaidag.fields?.EindDatum;
                if (selStart && selEind) {
                    found = reservations.find(r => r?.fields?.Start === selStart && r?.fields?.Eind === selEind);
                }
            }

            if (!found) {
                setCurrentReservation(null);
                setSelectedMaterials([]);
                setNotitie('');
                setGeenMateriaalNodig(false);
                refreshMaterialData();
            }
        }
    }, [reservations, selectedDraaidag, currentReservation, setCurrentReservation, refreshMaterialData]);

    // Preload functie voor snellere response
    const preloadData = useCallback(async () => {
        try {
            // Preload materialen in de achtergrond
            const response = await axios.get(API_ENDPOINTS.materials.list());
            setMaterialsCache({ data: response.data, timestamp: Date.now() });
        } catch (error) {
            // Preload failed (non-critical)
        }
    }, []);

    // Preload data bij component mount
    useEffect(() => {
        preloadData();
    }, [preloadData]);

    // Periodic sync voor data consistentie (elke 30 seconden)
    useEffect(() => {
        if (!selectedDraaidag) return;

        const syncInterval = setInterval(() => {
            // Silent sync - geen loading state, alleen data update
            fetchReservations(true);
        }, 30000); // 30 seconden

        return () => clearInterval(syncInterval);
    }, [selectedDraaidag, fetchReservations]);

    // In de component, voeg de handler toe voor het kopieren:
    const handleCopyMaterials = useCallback(async (materials) => {
        try {

            // Filter materialen die al in de huidige selectie zitten
            const existingMaterialIds = new Set(selectedMaterials.map(m => m.id));
            const newMaterials = materials.filter(m => !existingMaterialIds.has(m.id));

            if (newMaterials.length === 0) {
                toast('Alle geselecteerde materialen zijn al toegevoegd', { icon: 'ℹ️' });
                setCopyDialogOpen(false);
                return;
            }

            // Voeg de nieuwe materialen toe aan de huidige selectie
            const updatedMaterials = [...selectedMaterials, ...newMaterials];

            // Sla de reservering op met de nieuwe materialen
            const startDate = selectedDraaidag?.fields?.Start || selectedDraaidag?.fields?.StartDatum;
            const endDate = selectedDraaidag?.fields?.Eind || selectedDraaidag?.fields?.EindDatum;
            await handleSave(updatedMaterials, startDate, endDate);

            toast.success(`${newMaterials.length} nieuwe materialen succesvol gekopieerd!`);
            setCopyDialogOpen(false);

            // Refresh material data to show the newly copied materials with junction data
            if (selectedDraaidag) {
                await refreshMaterialData();
            }
        } catch (error) {
            console.error('Error copying materials:', error);
            toast.error('Fout bij kopieren van materialen');
        }
    }, [selectedMaterials, handleSave, selectedDraaidag, refreshMaterialData]);

    const [savingSet, setSavingSet] = useState(false);
    const handleSaveAsSet = async () => {
        if (!newSetName.trim()) {
            toast.error('Geef de materiaalset een naam.');
            return;
        }

        const materialIds = selectedMaterials.map(m => m.id);
        if (materialIds.length === 0) {
            toast.error('Er zijn geen materialen in deze reservering om op te slaan als set.');
            return;
        }

        setSavingSet(true);
        const toastId = toast.loading('Materiaalset opslaan...');
        try {
            // Hoofdletter-A is belangrijk: de auth-interceptor (index.js) zet het token alleen
            // op '/Api/'-paden. Met '/api/' ging dit verzoek zonder inlog weg → 401 → "Fout
            // bij het aanmaken" (wiki-feedback Robin, 6 okt 2026).
            await axios.post(`${BACKEND_URL}/Api/materiaalsets`, {
                name: newSetName,
                materialIds: materialIds
            });
            toast.success(`Materiaalset "${newSetName}" succesvol aangemaakt!`, { id: toastId });
            setSaveAsSetDialogOpen(false);
            setNewSetName('');
            fetchMateriaalsets();
        } catch (error) {
            console.error('Error creating material set:', error);
            const reden = error.response?.data?.detail || error.response?.data?.error;
            toast.error(`Fout bij het aanmaken van de materiaalset${typeof reden === 'string' && reden ? `: ${reden}` : '.'}`, { id: toastId });
        } finally {
            setSavingSet(false);
        }
    };

    if (error) return <Typography sx={{ fontFamily: T.fontBody, color: T.error }}>Fout: {error}</Typography>;
    if (!selectedDraaidag) return <Typography sx={{ fontFamily: T.fontBody, color: T.outline }}>Selecteer een draaidag.</Typography>;

    // Voortgang (gepakt) voor het reserveringsblok
    const totalMatMain = (selectedMaterials || []).length;
    const gepaktMatMain = (selectedMaterials || []).filter(m => (m.fields?.JunctionStatus || []).includes('Gepakt')).length;
    const pctMain = totalMatMain ? Math.round((gepaktMatMain / totalMatMain) * 100) : 0;
    const allPackedMain = totalMatMain > 0 && gepaktMatMain === totalMatMain;
    const fmtResDate = (s) => { try { return new Date(s + 'T12:00:00').toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }); } catch { return s; } };
    const reserveerder = currentReservation?.fields?.ModifiedByName || currentReservation?.fields?.SubmitterName;

  return (
        <Box sx={{ maxWidth: '1080px', mx: 'auto', pb: { xs: 4, sm: 3 } }}>
                {/* Back button */}
                <Box
                    onClick={handleGoBack}
                    sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.5,
                        mb: 1.5, color: T.primary, fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.82rem',
                        cursor: 'pointer', borderRadius: '0.5rem', px: 1.25, py: 0.6,
                        transition: 'all 0.15s ease',
                        '&:hover': { bgcolor: T.primaryContainer },
                    }}
                >
                    <Icon name="arrow_back" size={16} />
                    Terug naar kalender
                </Box>

                {/* Reserveringskader: kop (titel/datum) + action bar + materiaalgroepen */}
                <Box sx={{ bgcolor: T.surface, border: `1px solid ${T.surfaceHigh}`, borderRadius: '0.9rem', boxShadow: T.shadow, overflow: 'hidden', mb: 2 }}>
                {selectedDraaidag?.__standalone ? (
                    <Box sx={{ px: { xs: 2, sm: 2.5 }, pt: 2, pb: 1.75 }}>
                        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.62rem', fontWeight: 800, color: '#d4943a', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.5 }}>Losse reservering</Typography>
                        <Typography sx={{ fontFamily: T.font, fontSize: '1.35rem', fontWeight: 800, color: T.text, letterSpacing: '-0.02em', lineHeight: 1.15 }}>
                            {selectedDraaidag.fields?.Draaidag || 'Losse reservering'}
                        </Typography>
                        {!moveMode && (selectedDraaidag.fields?.StartDatum || selectedDraaidag.fields?.Start) && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                                <Icon name="event" size={15} sx={{ color: T.outline }} />
                                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.8rem', color: T.secondary }}>
                                    {(() => { const s = selectedDraaidag.fields?.StartDatum || selectedDraaidag.fields?.Start; const e = selectedDraaidag.fields?.EindDatum || selectedDraaidag.fields?.Eind; const f = (x) => { try { return new Date(x + 'T12:00:00').toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'long' }); } catch { return x; } }; return e && e !== s ? `${f(s)} – ${f(e)}` : f(s); })()}
                                </Typography>
                                {currentReservation?.id && (
                                    <Box component="button" onClick={() => { const s = currentReservation.fields?.Start || selectedDraaidag.fields?.Start || ''; const e = currentReservation.fields?.Eind || selectedDraaidag.fields?.Eind || s; setMoveStart(s); setMoveEnd(e); setMoveMode(true); }}
                                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, ml: 0.5, px: 1, py: 0.3, border: `1px solid ${T.outlineVariant}60`, borderRadius: '0.5rem', bgcolor: T.surface, fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.72rem', color: T.secondary, cursor: 'pointer', transition: 'all 0.15s', '&:hover': { bgcolor: T.surfaceHigh, borderColor: T.outline } }}>
                                        <Icon name="event_repeat" size={14} sx={{ color: T.outline }} /> Verplaatsen
                                    </Box>
                                )}
                            </Box>
                        )}
                        {moveMode && (
                            <Box sx={{ mt: 0.75, p: 1.25, borderRadius: '0.6rem', bgcolor: T.surface, border: `1px solid ${T.surfaceHigh}` }}>
                                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.7rem', fontWeight: 700, color: T.secondary, mb: 0.75 }}>Reservering verplaatsen naar</Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                    <TextField size="small" type="date" label="Van" InputLabelProps={{ shrink: true }} value={moveStart} onChange={(e) => setMoveStart(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '0.55rem', fontFamily: T.fontBody } }} />
                                    <TextField size="small" type="date" label="Tot en met" InputLabelProps={{ shrink: true }} value={moveEnd} onChange={(e) => setMoveEnd(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '0.55rem', fontFamily: T.fontBody } }} />
                                    <Box component="button" onClick={() => !isSaving && applyMoveReservation(moveStart, moveEnd)} disabled={isSaving}
                                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, px: 1.75, py: 0.7, border: 'none', borderRadius: '0.55rem', fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.78rem', color: '#fff', cursor: isSaving ? 'not-allowed' : 'pointer', background: `linear-gradient(135deg, ${T.tertiary} 0%, ${T.primary} 100%)` }}>
                                        <Icon name="check" size={15} sx={{ color: '#fff' }} /> {isSaving ? 'Bezig…' : 'Toepassen'}
                                    </Box>
                                    <Box component="button" onClick={() => { setMoveMode(false); }} disabled={isSaving}
                                        sx={{ px: 1.5, py: 0.7, border: 'none', borderRadius: '0.55rem', fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.78rem', color: T.secondary, bgcolor: 'transparent', cursor: 'pointer', '&:hover': { bgcolor: T.surfaceLow } }}>
                                        Annuleren
                                    </Box>
                                </Box>
                            </Box>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 1.25, p: 1.25, borderRadius: '0.6rem', bgcolor: '#fef3c7', border: '1px solid #f59e0b40' }}>
                            <Icon name="info" size={16} sx={{ color: '#d4943a', marginTop: '1px' }} />
                            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: '#7a5b1e', lineHeight: 1.5 }}>
                                Dit is <strong>geen draaidag</strong> — deze reservering staat niet in Airtable of in agenda's. Hij blokkeert wel materiaal voor de gekozen periode.
                            </Typography>
                        </Box>
                    </Box>
                ) : (
                    <DraaidagPreview />
                )}

                {/* Grijs reserveringsblok — altijd onder de witte draaidaggegevens */}
                {isDataLoaded && (
                    <Box sx={{ borderTop: `1px solid ${T.surfaceHigh}`, bgcolor: T.surfaceLow, px: { xs: 1.5, sm: 2.5 }, pt: 1.75, pb: 2 }}>
                        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.62rem', fontWeight: 800, color: T.outline, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1.25 }}>
                            Reservering
                        </Typography>

                        {/* Geen reservering — prompt */}
                        {!currentReservation && !geenMateriaalNodig && (
                            <Box sx={{ p: { xs: 3, sm: 4 }, bgcolor: T.surface, border: `1px solid ${T.surfaceHigh}`, borderRadius: '0.75rem', textAlign: 'center' }}>
                                <Icon name="inventory_2" size={40} sx={{ color: T.outlineVariant, display: 'block', margin: '0 auto 12px' }} />
                                <Typography sx={{ fontFamily: T.font, fontSize: '1.05rem', fontWeight: 700, color: T.text, mb: 0.5 }}>
                                    Nog geen reservering
                                </Typography>
                                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: T.outline, mb: 3 }}>
                                    Wat wil je doen voor deze draaidag?
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                                    <Box component="button" onClick={handleOpenWizard}
                                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 3, py: 1.25, border: 'none', borderRadius: '0.75rem', fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.88rem', color: '#fff', cursor: 'pointer', background: `linear-gradient(135deg, ${T.tertiary} 0%, ${T.primary} 100%)`, boxShadow: '0 4px 12px rgba(99,89,131,0.25)', transition: 'all 0.2s', '&:hover': { boxShadow: '0 6px 20px rgba(99,89,131,0.35)', transform: 'translateY(-1px)' } }}>
                                        <Icon name="add" size={20} sx={{ color: '#fff' }} />
                                        Maak reservering
                                    </Box>
                                    <Tooltip title="Kopieer de materialenlijst van een andere draaidag" arrow enterDelay={300}>
                                        <Box component="button" onClick={() => setCopyDialogOpen(true)}
                                            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 3, py: 1.25, bgcolor: 'transparent', border: `1.5px solid ${T.primaryContainer}`, borderRadius: '0.75rem', fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.88rem', color: T.primary, cursor: 'pointer', transition: 'all 0.15s', '&:hover': { bgcolor: T.primaryContainer } }}>
                                            <Icon name="download" size={20} sx={{ color: T.primary }} />
                                            Overnemen van...
                                        </Box>
                                    </Tooltip>
                                    <Box component="button" onClick={handleGeenMateriaalNodigChange}
                                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 3, py: 1.25, bgcolor: 'transparent', border: `1.5px solid ${T.outlineVariant}50`, borderRadius: '0.75rem', fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.88rem', color: T.secondary, cursor: 'pointer', transition: 'all 0.15s', '&:hover': { bgcolor: T.surfaceHigh, borderColor: T.outline } }}>
                                        <Icon name="block" size={20} sx={{ color: T.outline }} />
                                        Geen materiaal nodig
                                    </Box>
                                </Box>
                            </Box>
                        )}

                        {/* Geen materiaal nodig — banner */}
                        {geenMateriaalNodig && (
                            <Box sx={{ p: 3, bgcolor: T.emeraldBg, borderRadius: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Icon name="check_circle" size={22} sx={{ color: T.emerald }} />
                                    <Box>
                                        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.88rem', fontWeight: 700, color: '#065f46' }}>
                                            Geen materiaal nodig
                                        </Typography>
                                        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: '#065f46' }}>
                                            Deze draaidag is gemarkeerd als zonder materiaal
                                        </Typography>
                                    </Box>
                                    {opslaanStatus === 'saving' && <Icon name="hourglass_empty" size={16} sx={{ color: T.amber }} />}
                                </Box>
                                <Box component="button" onClick={handleGeenMateriaalNodigChange}
                                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 2, py: 0.75, bgcolor: 'transparent', border: `1.5px solid #065f4630`, borderRadius: '0.5rem', fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.78rem', color: '#065f46', cursor: 'pointer', transition: 'all 0.15s', '&:hover': { bgcolor: 'rgba(5,95,70,0.08)' } }}>
                                    Ongedaan maken
                                </Box>
                            </Box>
                        )}

                        {/* Met reservering */}
                        {currentReservation && !geenMateriaalNodig && (<>
                        {notitieStatus === 'error' && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25, mb: 2, borderRadius: '0.75rem', bgcolor: T.errorBg, border: `1px solid ${T.error}20` }}>
                                <Icon name="error" size={18} sx={{ color: T.error }} />
                                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.8rem', color: T.error }}>Fout bij opslaan notitie</Typography>
                                <Box onClick={() => setNotitieStatus('')} sx={{ ml: 'auto', cursor: 'pointer', display: 'flex', alignItems: 'center', color: T.error, '&:hover': { opacity: 0.7 } }}>
                                    <Icon name="close" size={16} />
                                </Box>
                            </Box>
                        )}
                        {/* Reservering-header */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', mb: 1.5 }}>
                            <Typography sx={{ fontFamily: T.font, fontSize: '0.98rem', fontWeight: 800, color: T.text }}>
                                #{currentReservation.fields.reserveringNummer}
                            </Typography>
                            {currentReservation.fields.Start && (
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.35 }}>
                                    <Icon name="event" size={14} sx={{ color: T.outline }} />
                                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', fontWeight: 600, color: T.secondary }}>
                                        {fmtResDate(currentReservation.fields.Start)}{currentReservation.fields.Eind && currentReservation.fields.Eind !== currentReservation.fields.Start && ` – ${fmtResDate(currentReservation.fields.Eind)}`}
                                    </Typography>
                                </Box>
                            )}
                            {reserveerder && (
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.35 }}>
                                    <Icon name="person" size={14} sx={{ color: T.outline }} />
                                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', fontWeight: 600, color: T.secondary }}>
                                        {reserveerder}
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        {/* Action bar */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                            <Tooltip title="Materialen toevoegen of verwijderen" arrow>
                                <Box component="button" onClick={handleOpenWizard} disabled={isSaving}
                                    sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 0.6, px: 2.25, py: 1, border: 'none', borderRadius: '0.6rem', fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.85rem', color: '#fff', cursor: isSaving ? 'not-allowed' : 'pointer', background: isSaving ? T.outlineVariant : `linear-gradient(135deg, ${T.tertiary} 0%, ${T.primary} 100%)`, boxShadow: '0 2px 8px rgba(99,89,131,0.2)', transition: 'all 0.15s', '&:hover': { boxShadow: isSaving ? 'none' : '0 4px 16px rgba(99,89,131,0.3)' } }}>
                                    <Icon name="edit_note" size={18} sx={{ color: '#fff' }} />
                                    {isSaving ? "Opslaan..." : "Wijzig reservering"}
                                </Box>
                            </Tooltip>
                            {/* Kopiëren en opslaan als set zaten alleen in het ⋮-menu; niemand vond ze
                                (wiki-feedback Materiaal, 1 sep). Nu ook als zichtbare knop. */}
                            <Tooltip title="Deze materialenlijst overnemen op een of meer andere draaidagen" arrow>
                                <Box component="button" onClick={!isSaving ? () => setCopyToDialogOpen(true) : undefined}
                                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.9, border: `1.5px solid ${T.primaryContainer}`, borderRadius: '0.6rem', bgcolor: 'transparent', fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.82rem', color: T.primary, cursor: 'pointer', '&:hover': { bgcolor: T.primaryContainer } }}>
                                    <Icon name="upload" size={16} sx={{ color: T.primary }} /> Kopiëren naar…
                                </Box>
                            </Tooltip>
                            <Tooltip title="Deze lijst bewaren als standaardset, bijvoorbeeld voor deze klant" arrow>
                                <Box component="button" onClick={!isSaving ? () => { setNewSetName((v) => v || (optimisticDraaidagData?.klantNaam ? `${optimisticDraaidagData.klantNaam} — standaardset` : '')); setSaveAsSetDialogOpen(true); } : undefined}
                                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.9, border: `1.5px solid ${T.primaryContainer}`, borderRadius: '0.6rem', bgcolor: 'transparent', fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.82rem', color: T.primary, cursor: 'pointer', '&:hover': { bgcolor: T.primaryContainer } }}>
                                    <Icon name="save" size={16} sx={{ color: T.primary }} /> Opslaan als set
                                </Box>
                            </Tooltip>
                            <Tooltip title="Materiaal 1 dag eerder reserveren (voorbereiding)" arrow>
                                <Box component="button" onClick={!isSaving ? handleExtraDagVoorChange : undefined}
                                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, px: 1.25, py: 0.9, border: `1.5px solid ${extraDagVoor ? T.primary : T.outlineVariant + '60'}`, borderRadius: '0.6rem', bgcolor: extraDagVoor ? T.primaryContainer : T.surface, fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.8rem', color: extraDagVoor ? T.primary : T.outline, cursor: 'pointer', transition: 'all 0.15s', '&:hover': { bgcolor: extraDagVoor ? T.primaryContainer : T.surfaceHigh } }}>
                                    <Icon name="remove" size={15} /> 1 dag
                                </Box>
                            </Tooltip>
                            <Tooltip title="Materiaal 1 dag langer reserveren (late terugkomst)" arrow>
                                <Box component="button" onClick={!isSaving ? handleExtraDagNaChange : undefined}
                                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, px: 1.25, py: 0.9, border: `1.5px solid ${extraDagNa ? T.amber : T.outlineVariant + '60'}`, borderRadius: '0.6rem', bgcolor: extraDagNa ? T.amberBg : T.surface, fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.8rem', color: extraDagNa ? '#92400e' : T.outline, cursor: 'pointer', transition: 'all 0.15s', '&:hover': { bgcolor: extraDagNa ? T.amberBg : T.surfaceHigh } }}>
                                    <Icon name="add" size={15} /> 1 dag
                                </Box>
                            </Tooltip>
                            <Tooltip title="Meer acties" arrow>
                                <Box component="button" onClick={(e) => setMoreAnchor(e.currentTarget)}
                                    sx={{ width: 40, height: 40, flexShrink: 0, borderRadius: '0.6rem', border: `1.5px solid ${T.outlineVariant}50`, bgcolor: T.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.15s', '&:hover': { bgcolor: T.surfaceHigh } }}>
                                    <Icon name="more_vert" size={20} sx={{ color: T.outline }} />
                                </Box>
                            </Tooltip>
                        </Box>

                        {/* Progress */}
                        {isDataLoaded && (
                            <Box sx={{ mb: 3 }}>
                                {totalMatMain > 0 ? (
                                    <>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.45 }}>
                                                <Icon name={allPackedMain ? 'check_circle' : 'inventory_2'} size={16} sx={{ color: allPackedMain ? T.emerald : T.outline }} />
                                                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.8rem', fontWeight: 800, color: allPackedMain ? T.emerald : T.secondary }}>
                                                    {allPackedMain ? 'Alles gepakt' : `${gepaktMatMain}/${totalMatMain} gepakt`}
                                                </Typography>
                                            </Box>
                                            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: 700, color: T.outline }}>{pctMain}%</Typography>
                                        </Box>
                                        <Box sx={{ height: 7, borderRadius: '9999px', bgcolor: T.surfaceHigh, overflow: 'hidden' }}>
                                            <Box sx={{ width: `${pctMain}%`, height: '100%', bgcolor: T.emerald, borderRadius: '9999px', transition: 'width 0.35s ease' }} />
                                        </Box>
                                    </>
                                ) : (
                                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.outline }}>Nog geen materiaal toegevoegd</Typography>
                                )}
                            </Box>
                        )}

                        {/* Notitie */}
                        <Box sx={{ mb: 0.5 }}>
                            <TextField label="Notitie" value={notitie} onChange={e => setNotitie(e.target.value)}
                                onBlur={handleNotitieSave} onKeyPress={handleNotitieKeyPress}
                                multiline minRows={1} fullWidth size="small" variant="outlined"
                                placeholder="Type hier je notitie (Ctrl+Enter om op te slaan)"
                                InputProps={{ endAdornment: notitieStatus === 'saving' ? (<Icon name="hourglass_empty" size={16} sx={{ color: T.amber }} />) : notitieStatus === 'saved' ? (<Icon name="check_circle" size={16} sx={{ color: T.emerald }} />) : notitieStatus === 'error' ? (<Icon name="error" size={16} sx={{ color: T.error }} />) : null }}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '0.5rem', bgcolor: T.surface, fontFamily: T.fontBody } }}
                            />
                        </Box>
                        <GereserveerdMateriaalLijst
                            reservedMaterials={selectedMaterials}
                            setOpenWizard={setOpenWizard}
                            updateJunctionStatus={updateJunctionStatusWithLegacySupport}
                            typeById={typeById}
                            onOpenTypeInfo={handleOpenTypeInfo}
                            onRemoveMaterial={(material) => handleSave(selectedMaterials.filter(m => m.id !== material.id))}
                            onRestoreMaterial={(material) => handleSave(selectedMaterials.some(m => m.id === material.id) ? selectedMaterials : [...selectedMaterials, material])}
                            setCurrentStepCategorie={(index) => {
                                setSelectedCategoryIndex(index);
                                setOpenWizard(true);
                            }}
                            currentStepCategorie={0}
                            handleOpenScanner={() => {}}
                        />
                        </>)}
                    </Box>
                )}
                </Box>

                <SimpleMaterialWizard
                    openWizard={openWizard}
                    handleCloseWizard={handleCloseWizard}
                    materialen={alleMaterialen}
                    selectedMaterials={selectedMaterials}
                    setSelectedMaterials={setSelectedMaterials}
                    onSave={handleSave}
                    currentReservation={currentReservationMemo}
                    geselecteerdeDraaidag={selectedDraaidag}
                    reservationsData={processReservationsData(reservations)}
                    reservations={reservations}
                    initialCategory={selectedCategoryIndex}
                    materiaalsets={materiaalsets}
                    typeById={typeById}
                    onOpenTypeInfo={handleOpenTypeInfo}
                />

                {/* Info-popup: compleet materiaal (type) van een exemplaar — hergebruik materiaalbeheer */}
                <TypePopup
                    open={!!infoGroup}
                    group={infoGroup}
                    allSets={materiaalsets}
                    onClose={() => setInfoTypeId(null)}
                    onChanged={refreshTypesAndMaterials}
                    onExemplaarClick={(mat) => setInfoExemplaarId(mat.id)}
                    getMaterialReservations={getMaterialReservationsForPopup}
                />
                <ExemplaarPopup
                    open={!!infoExemplaar}
                    material={infoExemplaar}
                    onClose={() => setInfoExemplaarId(null)}
                    onChanged={refreshTypesAndMaterials}
                    getMaterialReservations={getMaterialReservationsForPopup}
                />

                {/* Update de CopyMaterialsDialog met de juiste props */}
                <CopyMaterialsDialog
                    open={copyDialogOpen}
                    onClose={() => setCopyDialogOpen(false)}
                    currentDraaidag={{
                        ...selectedDraaidag,
                        currentReservationId: currentReservation?.id
                    }}
                    onCopy={handleCopyMaterials}
                    reservations={reservations}
                />

                <CopyToOtherDraaidagenDialog
                    open={copyToDialogOpen}
                    onClose={() => setCopyToDialogOpen(false)}
                    draaidagen={draaidagen}
                    currentDraaidagId={selectedDraaidag?.id}
                    selectedMaterials={selectedMaterials}
                    currentReservation={currentReservation}
                    fetchReservations={fetchReservations}
                    extraDagVoor={extraDagVoor}
                    extraDagNa={extraDagNa}
                />

                <ClashApprovalDialog
                    open={clashDialogOpen}
                    onClose={handleClashCancel}
                    clashes={clashGroups}
                    materialen={alleMaterialen}
                    reservationsData={processReservationsData(reservations)}
                    extendedStart={pendingMove?.newStart ?? pendingExtension?.effStart}
                    extendedEnd={pendingMove?.newEnd ?? pendingExtension?.effEnd}
                    currentReservationId={currentReservation?.id}
                    reservedMaterialIds={pendingMove?.materialIds || pendingExtension?.materialIds || []}
                    onConfirm={handleClashConfirm}
                />

                <Dialog open={saveAsSetDialogOpen} onClose={() => setSaveAsSetDialogOpen(false)} PaperProps={{ sx: { borderRadius: '0.75rem' } }}>
                    <DialogTitle sx={{ fontFamily: T.font, fontWeight: 700, color: T.text }}>Opslaan als Materiaalset</DialogTitle>
                    <DialogContent>
                        <DialogContentText sx={{ fontFamily: T.fontBody, color: T.secondary }}>
                            Voer een naam in voor deze nieuwe materiaalset. De set zal alle{' '}
                            {selectedMaterials.length} materialen van de huidige reservering bevatten.
                        </DialogContentText>
                        <TextField
                            autoFocus
                            margin="dense"
                            id="name"
                            label="Naam van de set"
                            type="text"
                            fullWidth
                            variant="standard"
                            value={newSetName}
                            onChange={(e) => setNewSetName(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSaveAsSet()}
                            sx={{
                                '& .MuiInput-root': { fontFamily: T.fontBody },
                                '& .MuiInputLabel-root': { fontFamily: T.fontBody },
                            }}
                        />
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Box
                            component="button"
                            onClick={() => setSaveAsSetDialogOpen(false)}
                            disabled={savingSet}
                            sx={{
                                px: 2, py: 0.65, bgcolor: 'transparent',
                                border: `1px solid ${T.outlineVariant}`, borderRadius: '0.5rem',
                                fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.78rem',
                                color: T.secondary, cursor: savingSet ? 'not-allowed' : 'pointer',
                                transition: 'all 0.15s ease',
                                '&:hover': { bgcolor: T.surfaceLow },
                            }}
                        >
                            Annuleren
                        </Box>
                        <Box
                            component="button"
                            onClick={handleSaveAsSet}
                            disabled={savingSet}
                            sx={{
                                px: 2, py: 0.65, border: 'none', borderRadius: '0.5rem',
                                fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.78rem',
                                color: '#fff', cursor: savingSet ? 'not-allowed' : 'pointer',
                                background: savingSet
                                    ? T.outlineVariant
                                    : `linear-gradient(135deg, ${T.tertiary} 0%, ${T.primary} 100%)`,
                                boxShadow: '0 1px 3px rgba(99,89,131,0.18)',
                                transition: 'all 0.15s ease',
                                opacity: savingSet ? 0.7 : 1,
                                '&:hover': {
                                    boxShadow: savingSet ? 'none' : '0 2px 8px rgba(99,89,131,0.25)',
                                },
                            }}
                        >
                            {savingSet ? 'Opslaan...' : 'Opslaan'}
                        </Box>
                    </DialogActions>
                </Dialog>

                {/* Bevestigingsdialog voor verwijderen */}
                <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} PaperProps={{ sx: { borderRadius: '0.75rem' } }}>
                    <DialogTitle sx={{ fontFamily: T.font, fontWeight: 700, color: T.text }}>Reservering verwijderen</DialogTitle>
                    <DialogContent>
                        <DialogContentText sx={{ fontFamily: T.fontBody, color: T.secondary }}>
                            Weet je zeker dat je deze reservering wilt verwijderen?
                            Dit kan niet ongedaan worden gemaakt.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Box
                            component="button"
                            onClick={() => setDeleteDialogOpen(false)}
                            sx={{
                                px: 2, py: 0.65, bgcolor: 'transparent',
                                border: `1px solid ${T.outlineVariant}`, borderRadius: '0.5rem',
                                fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.78rem',
                                color: T.secondary, cursor: 'pointer',
                                transition: 'all 0.15s ease',
                                '&:hover': { bgcolor: T.surfaceLow },
                            }}
                        >
                            Annuleren
                        </Box>
                        <Box
                            component="button"
                            onClick={() => {
                                handleDeleteReservation(currentReservation.id);
                                setDeleteDialogOpen(false);
                            }}
                            sx={{
                                px: 2, py: 0.65, border: 'none', borderRadius: '0.5rem',
                                fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.78rem',
                                color: '#fff', cursor: 'pointer',
                                bgcolor: T.error,
                                boxShadow: '0 1px 3px rgba(168,56,54,0.18)',
                                transition: 'all 0.15s ease',
                                '&:hover': {
                                    boxShadow: '0 2px 8px rgba(168,56,54,0.25)',
                                },
                            }}
                        >
                            Verwijderen
                        </Box>
                    </DialogActions>
                </Dialog>

                {/* "Meer"-menu — secundaire acties */}
                <Menu anchorEl={moreAnchor} open={Boolean(moreAnchor)} onClose={() => setMoreAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    PaperProps={{ sx: { borderRadius: '0.75rem', minWidth: 220, boxShadow: '0 8px 30px rgba(45,51,53,0.15)', border: `1px solid ${T.surfaceHigh}`, mt: 0.5 } }}>
                    <MenuItem onClick={() => { setMoreAnchor(null); setCopyDialogOpen(true); }} sx={{ fontFamily: T.fontBody, fontSize: '0.85rem', color: T.text, py: 1.1, gap: 1.25 }}>
                        <Icon name="download" size={18} sx={{ color: T.outline }} /> Overnemen van…
                    </MenuItem>
                    {selectedMaterials.length > 0 && (
                        <MenuItem onClick={() => { setMoreAnchor(null); setCopyToDialogOpen(true); }} sx={{ fontFamily: T.fontBody, fontSize: '0.85rem', color: T.text, py: 1.1, gap: 1.25 }}>
                            <Icon name="upload" size={18} sx={{ color: T.outline }} /> Kopiëren naar…
                        </MenuItem>
                    )}
                    {selectedMaterials.length > 0 && (
                        <MenuItem onClick={() => { setMoreAnchor(null); setSaveAsSetDialogOpen(true); }} sx={{ fontFamily: T.fontBody, fontSize: '0.85rem', color: T.text, py: 1.1, gap: 1.25 }}>
                            <Icon name="save" size={18} sx={{ color: T.outline }} /> Opslaan als set
                        </MenuItem>
                    )}
                    <MenuItem onClick={() => { setMoreAnchor(null); handleGeenMateriaalNodigChange(); }} sx={{ fontFamily: T.fontBody, fontSize: '0.85rem', color: T.text, py: 1.1, gap: 1.25 }}>
                        <Icon name="block" size={18} sx={{ color: T.outline }} /> Geen materiaal nodig
                    </MenuItem>
                    <MenuItem onClick={() => { setMoreAnchor(null); setDeleteDialogOpen(true); }} sx={{ fontFamily: T.fontBody, fontSize: '0.85rem', color: T.error, py: 1.1, gap: 1.25, borderTop: `1px solid ${T.surfaceLow}` }}>
                        <Icon name="delete" size={18} sx={{ color: T.error }} /> Verwijder reservering
                    </MenuItem>
                </Menu>
            </Box>
  );
});
